import {
  EventSeriesSortKeys,
  EventSortKeys,
} from '@app/graphql/types-and-hooks';
import {
  defaultInviteMemberFormState,
  defaultState as defaultManageTeamState,
} from '@app/routes/organization/routes/manage/routes/team/domain';
import { defaultState as defaultTeamState } from '@app/routes/organization/routes/manage/routes/team/routes/active-member/domain';
import { defaultState as defaultPendingInvitesState } from '@app/routes/organization/routes/manage/routes/team/routes/pending-invites/domain';
import { mergeSpeakers } from '@app/utilities/utils';
import { produce } from 'immer';
import { Reducer } from 'redux';

import { defaultState as defaultAnalyticsState } from '../features/analytics/domain';
import { SObjectFields } from '../routes/apps/domain';
import {
  createDefaultContentComposerState,
  defaultState as defaultContentHubState,
} from '../routes/content/domain';
import {
  ContentHistoryItem,
  ContentRepurposingStatus,
  DEFAULT_TONE_OF_VOICE_ID,
} from '../routes/content/routes/recordings/components/content-composer/domain';
import { DEFAULT_VIDEO_BG_COLOR } from '../routes/content/routes/recordings/const';
import {
  createDefaultAdvancedEditsState,
  DraftClip,
  editListCutsFromDraft,
  MAX_HISTORY_SIZE,
} from '../routes/content/routes/recordings/domain';
import {
  ApiContentRepurposingStatus,
  ContentGenerationStatus,
  ContentHistoryEntry,
  ContentType,
  GeneratedContent,
  OPTIMISTIC_CONTENT_ID,
} from '../routes/content/routes/recordings/routes/lab/domain';
import {
  createDefaultContentHistory,
  createDefaultCreatorLabSelectedContent,
  isEmailContent,
  mapApiStatusToFrontend,
} from '../routes/content/routes/recordings/routes/lab/utils';
import {
  generateClipId,
  getClipPresetBackgroundColor,
  getColorIndex,
  getDefaultPreset,
  getPresetColor,
  hydrateAdvancedEditCutsFromRow,
  setDefaultPresetOnClip,
  syncClipProperty,
  syncLastUsedBackgroundColor,
} from '../routes/content/routes/recordings/utils';
import { defaultState as defaultEventWizardState } from '../routes/create-event-wizard/domain';
import { defaultState as defaultOrganizationCreateState } from '../routes/create-organization/domain';
import {
  defaultState as defaultOrganizationState,
  Event,
  EventSeries,
} from '../routes/organization/domain';
import { defaultState as defaultBillingState } from '../routes/organization/routes/manage/routes/billing/domain';
import { defaultCreateSpaceFormState } from '../routes/organization/routes/manage/routes/spaces/domain';
import { StoreEvent } from './event';
import {
  defaultSalesforceEcaMigrationState,
  defaultState,
  State,
} from './state';

export const reducer: Reducer<State, StoreEvent, State> = (state, event) => {
  if (!state) return defaultState;
  if (!event) return state;

  return produce(state, draft => {
    const ensureSelectedContent = () => {
      if (!draft.contentHub.creatorLab.selectedContent) {
        draft.contentHub.creatorLab.selectedContent =
          createDefaultCreatorLabSelectedContent('SOCIAL_POST');
      }

      const selectedContent = draft.contentHub.creatorLab.selectedContent;
      if (!selectedContent) {
        throw new Error('creatorLab.selectedContent should be defined');
      }

      return selectedContent;
    };

    const resetContentAttributes = (
      selectedContent: ReturnType<typeof ensureSelectedContent>,
    ) => {
      selectedContent.attributes.toneOfVoiceId = null;
      selectedContent.attributes.topicId = null;

      if (isEmailContent(selectedContent)) {
        selectedContent.attributes.emailAudienceId = null;
        selectedContent.attributes.emailTypeId = null;
      }
    };

    const initializeSelectedClip = (newClip: DraftClip | null) => {
      if (!newClip) return;
      const presets = draft.contentHub.recordingInfo.colorPresets.presets;

      if (!newClip.preset_id) {
        const defaultPreset = getDefaultPreset(presets);
        if (defaultPreset) newClip.preset_id = defaultPreset.id;
      }

      if (!newClip.lastUsedBackgroundColor) {
        newClip.lastUsedBackgroundColor =
          typeof newClip.background_color === 'string'
            ? newClip.background_color
            : getClipPresetBackgroundColor(newClip.preset_id, presets);
      }

      if (newClip.include_captions !== undefined) {
        draft.contentHub.recordingInfo.captions.enabled =
          newClip.include_captions;
      }
    };

    switch (event.type) {
      case 'reactive-map - auth token updated': {
        draft.user.authToken = event.payload.authToken;
        break;
      }
      case 'reactive-map - user updated': {
        draft.user.id = event.payload.id;
        draft.user.email = event.payload.email;
        break;
      }
      case 'reactive-map - feature flags updated': {
        draft.featureFlags = event.payload.featureFlags;
        break;
      }
      case 'download-url - fetch started': {
        draft.fileDownloadUrls[event.payload.filename] = {
          url: draft.fileDownloadUrls[event.payload.filename]?.url ?? null,
          expiresAt:
            draft.fileDownloadUrls[event.payload.filename]?.expiresAt ?? null,
          loading: true,
          error: null,
        };
        break;
      }
      case 'download-url - fetch success': {
        draft.fileDownloadUrls[event.payload.filename] = {
          url: event.payload.url,
          expiresAt: event.payload.expiresAt,
          loading: false,
          error: null,
        };
        break;
      }
      case 'download-url - fetch failed': {
        draft.fileDownloadUrls[event.payload.filename] = {
          url: null,
          expiresAt: null,
          loading: false,
          error: event.payload.error,
        };
        break;
      }
      case 'reactive-map - organization changed': {
        draft.organizationExternalId = event.payload.organizationExternalId;
        draft.apps.salesforceData.ecaMigration = {
          ...defaultSalesforceEcaMigrationState,
        };
        draft.contentHub = defaultContentHubState;
        draft.createOrganization = defaultOrganizationCreateState;
        draft.organization = {
          ...defaultOrganizationState,
          events: {
            status: 'idle',
            filters: {
              searchText: '',
              eventStatus: [],
            },
            items: [],
            pageInfo: {
              hasNextPage: false,
              endCursor: null,
            },
            features: {
              eventsLite: false,
              eventsPro: false,
            },
            error: {
              message: '',
            },
            sort: {
              sortField: EventSortKeys.TimeStart,
              reverseSort: true,
            },
            loadFirstPage: true,
          },
          series: {
            expandedSeriesId: '',
            status: 'idle',
            filters: {
              searchText: '',
            },
            items: [],
            pageInfo: {
              hasNextPage: false,
              endCursor: null,
            },
            error: {
              message: '',
            },
            sort: {
              sortField: EventSeriesSortKeys.Name,
              reverseSort: false,
            },
            loadFirstPage: true,
          },
          manageTeam: defaultManageTeamState,
          teamActiveMembers: defaultTeamState,
          teamPendingInvites: defaultPendingInvitesState,
          orgSpaces: {
            status: 'idle',
            spaces: [],
            pageInfo: {
              hasNextPage: false,
              endCursor: null,
            },
          },
          spacesFeatureFlagEnabled: false,
          invalidSpaceAccessModal: {
            isOpen: false,
            previousSpaceName: '',
            newSpaceName: '',
          },
        };
        draft.navBar.isOrgSelected = true;
        draft.billing = defaultBillingState;
        break;
      }
      case '[organization] org space changed': {
        draft.organization = {
          ...draft.organization,
          currentSpace: {
            id: event.payload.spaceExternalId,
            name: event.payload.spaceName,
          },
          events: {
            status: 'idle',
            filters: {
              searchText: '',
              eventStatus: [],
            },
            items: [],
            pageInfo: {
              hasNextPage: false,
              endCursor: null,
            },
            features: {
              eventsLite: false,
              eventsPro: false,
            },
            error: {
              message: '',
            },
            sort: {
              sortField: EventSortKeys.TimeStart,
              reverseSort: true,
            },
            loadFirstPage: true,
          },
          series: {
            expandedSeriesId: '',
            status: 'idle',
            filters: {
              searchText: '',
            },
            items: [],
            pageInfo: {
              hasNextPage: false,
              endCursor: null,
            },
            error: {
              message: '',
            },
            sort: {
              sortField: EventSeriesSortKeys.Name,
              reverseSort: false,
            },
            loadFirstPage: true,
          },
        };
        break;
      }
      case '[org-spaces] flag enabled': {
        draft.organization.spacesFeatureFlagEnabled = true;
        break;
      }
      case '[nav-bar] organization selected': {
        draft.navBar.isOrgSelected = event.payload.isOrgSelected;
        if (event.payload.selectedOrgInfo) {
          draft.navBar.selectedOrgInfo = event.payload.selectedOrgInfo;
        }
        break;
      }
      case '[nav-bar] user-profile fetch started': {
        draft.navBar.userProfile.status = 'processing';
        draft.navBar.userProfile.error = null;
        break;
      }
      case '[nav-bar] user-profile fetch success': {
        draft.navBar.userProfile.data = event.payload.data;
        draft.navBar.userProfile.status = 'success';
        break;
      }
      case '[nav-bar] user-profile fetch failed': {
        draft.navBar.userProfile.status = 'failed';
        draft.navBar.userProfile.error = event.payload.error;
        break;
      }
      case '[nav-bar] user-profile terms accepted': {
        if (draft.navBar.userProfile.data) {
          draft.navBar.userProfile.data.hasAcceptedTerms = true;
        }
        break;
      }
      case '[nav-bar] organizations fetch started': {
        draft.navBar.organizations.status = 'processing';
        draft.navBar.organizations.error = null;
        break;
      }
      case '[nav-bar] organizations fetch success': {
        draft.navBar.organizations.data = event.payload.data;
        draft.navBar.organizations.pageInfo = event.payload.pageInfo;
        draft.navBar.organizations.status = 'success';
        break;
      }
      case '[nav-bar] organizations fetch failed': {
        draft.navBar.organizations.status = 'failed';
        draft.navBar.organizations.error = event.payload.error;
        break;
      }
      case '[nav-bar] vendor-booths fetch started': {
        draft.navBar.vendorBooths.status = 'processing';
        draft.navBar.vendorBooths.error = null;
        break;
      }
      case '[nav-bar] vendor-booths fetch success': {
        draft.navBar.vendorBooths.data = event.payload.data;
        draft.navBar.vendorBooths.pageInfo = event.payload.pageInfo;
        draft.navBar.vendorBooths.status = 'success';
        break;
      }
      case '[nav-bar] vendor-booths fetch failed': {
        draft.navBar.vendorBooths.status = 'failed';
        draft.navBar.vendorBooths.error = event.payload.error;
        break;
      }
      case '[nav-bar] update language picker open state': {
        draft.navBar.isLanguagePickerOpen = event.payload.isLanguagePickerOpen;
        break;
      }
      case '[nav-bar] update help menu open state': {
        draft.navBar.isHelpMenuOpen = event.payload.isHelpMenuOpen;
        break;
      }
      case '[nav-bar] update org switcher open state': {
        draft.navBar.isOrgSwitcherOpen = event.payload.isOrgSwitcherOpen;
        break;
      }
      case 'apps - open configuration panel': {
        draft.apps.editingAppsConfiguration = event.payload.app;
        break;
      }
      case 'apps - close configuration panel': {
        draft.apps.editingAppsConfiguration = undefined;
        draft.apps.salesforceData.ecaMigration = {
          ...defaultSalesforceEcaMigrationState,
        };
        break;
      }
      case '[salesforce-eca] reset': {
        const selectedAuthenticationId =
          draft.apps.salesforceData.ecaMigration.selectedAuthenticationId;
        draft.apps.salesforceData.ecaMigration = {
          ...defaultSalesforceEcaMigrationState,
          selectedAuthenticationId,
        };
        break;
      }
      case '[salesforce-eca] status loading': {
        draft.apps.salesforceData.ecaMigration.loading = true;
        draft.apps.salesforceData.ecaMigration.errorKey = null;
        break;
      }
      case '[salesforce-eca] status success': {
        draft.apps.salesforceData.ecaMigration.initialized = true;
        draft.apps.salesforceData.ecaMigration.loading = false;
        draft.apps.salesforceData.ecaMigration.migration =
          event.payload.migration;
        draft.apps.salesforceData.ecaMigration.errorKey = null;
        break;
      }
      case '[salesforce-eca] status failure': {
        draft.apps.salesforceData.ecaMigration.initialized = true;
        draft.apps.salesforceData.ecaMigration.loading = false;
        draft.apps.salesforceData.ecaMigration.errorKey =
          event.payload.errorKey;
        break;
      }
      case '[salesforce-eca] phase changed': {
        draft.apps.salesforceData.ecaMigration.finalizationPhase =
          event.payload.phase;
        break;
      }
      case '[salesforce-eca] error changed': {
        draft.apps.salesforceData.ecaMigration.errorKey =
          event.payload.errorKey;
        break;
      }
      case '[salesforce-eca] authentication required changed': {
        draft.apps.salesforceData.ecaMigration.authenticationRequired =
          event.payload.required;
        if (!event.payload.required) {
          draft.apps.salesforceData.ecaMigration.authenticationRecoveryErrorKey =
            null;
        }
        break;
      }
      case '[salesforce-eca] authentication recovery error changed': {
        draft.apps.salesforceData.ecaMigration.authenticationRecoveryErrorKey =
          event.payload.errorKey;
        break;
      }
      case '[salesforce-eca] warning changed': {
        draft.apps.salesforceData.ecaMigration.warningKey =
          event.payload.warningKey;
        break;
      }
      case '[salesforce-eca] authentication selected': {
        draft.apps.salesforceData.ecaMigration.selectedAuthenticationId =
          event.payload.authenticationId;
        break;
      }
      case '[salesforce-eca] previous authentication captured': {
        draft.apps.salesforceData.ecaMigration.previousAuthenticationId =
          event.payload.authenticationId;
        break;
      }
      case '[salesforce-eca] expected authentication changed': {
        draft.apps.salesforceData.ecaMigration.expectedAuthenticationId =
          event.payload.authenticationId;
        break;
      }
      case '[salesforce-eca] context refresh started': {
        const migration = draft.apps.salesforceData.ecaMigration;
        migration.contextRefreshGeneration += 1;
        migration.connectorRefreshGeneration += 1;
        migration.connectorRefreshPending = [];
        migration.connectorRefreshFailed = false;
        // bankQuestions is intentionally excluded: it's supplementary data for
        // registration field mapping and shouldn't block Salesforce ECA reconnection.
        migration.contextRefreshPending = [
          'solutionInstance',
          'authentications',
        ];
        migration.contextRefreshFailed = false;
        break;
      }
      case '[salesforce-eca] connector refresh started': {
        const migration = draft.apps.salesforceData.ecaMigration;
        migration.connectorRefreshPending = ['events', 'sobjects', 'campaigns'];
        migration.connectorRefreshFailed = false;
        break;
      }
      case '[salesforce-eca] finalization completed': {
        const migration = draft.apps.salesforceData.ecaMigration;
        migration.initialized = true;
        migration.loading = false;
        migration.errorKey = null;
        migration.expectedAuthenticationId = null;
        migration.authenticationRequired = false;
        migration.authenticationRecoveryErrorKey = null;
        migration.migration = {
          target: migration.migration?.target,
          required: false,
          status: 'completed',
          action: null,
          installUrls: migration.migration?.installUrls,
          cleanupStatus: migration.migration?.cleanupStatus,
        };
        break;
      }
      case 'apps - fetch events loading': {
        draft.apps.events.loading = true;
        draft.apps.events.error = null;
        break;
      }
      case 'apps - fetch events success': {
        draft.apps.events.loading = false;
        draft.apps.events.items = event.payload.events ?? [];
        draft.apps.events.error = null;
        draft.apps.salesforceData.ecaMigration.connectorRefreshPending =
          draft.apps.salesforceData.ecaMigration.connectorRefreshPending.filter(
            request => request !== 'events',
          );
        break;
      }
      case 'apps - fetch events failure': {
        draft.apps.events.loading = false;
        draft.apps.events.items = [];
        draft.apps.events.error = event.payload.error;
        draft.apps.salesforceData.ecaMigration.connectorRefreshFailed = true;
        draft.apps.salesforceData.ecaMigration.connectorRefreshPending =
          draft.apps.salesforceData.ecaMigration.connectorRefreshPending.filter(
            request => request !== 'events',
          );
        break;
      }
      case 'apps - fetch hubs loading': {
        draft.apps.hubs.loading = true;
        draft.apps.hubs.error = null;
        break;
      }
      case 'apps - fetch hubs success': {
        draft.apps.hubs.loading = false;
        draft.apps.hubs.items = event.payload.hubs ?? [];
        draft.apps.hubs.error = null;
        break;
      }
      case 'apps - fetch hubs failure': {
        draft.apps.hubs.loading = false;
        draft.apps.hubs.items = [];
        draft.apps.hubs.error = event.payload.error;
        break;
      }
      case 'apps - fetch authentications loading': {
        draft.apps.authentications.loading = true;
        draft.apps.authentications.error = null;
        break;
      }
      case 'apps - fetch authentications success': {
        draft.apps.authentications.loading = false;
        draft.apps.authentications.items = event.payload.authentications ?? [];
        draft.apps.authentications.error = null;
        draft.apps.salesforceData.ecaMigration.contextRefreshPending =
          draft.apps.salesforceData.ecaMigration.contextRefreshPending.filter(
            request => request !== 'authentications',
          );
        break;
      }
      case 'apps - fetch authentications failure': {
        draft.apps.authentications.loading = false;
        draft.apps.authentications.items = [];
        draft.apps.authentications.error = event.payload.error;
        draft.apps.salesforceData.ecaMigration.contextRefreshFailed = true;
        draft.apps.salesforceData.ecaMigration.contextRefreshPending = [];
        break;
      }
      case 'apps - fetch solution instance loading': {
        draft.apps.solutionInstance.loading = true;
        draft.apps.solutionInstance.error = null;
        break;
      }
      case 'apps - fetch solution instance success': {
        draft.apps.solutionInstance.loading = false;
        draft.apps.solutionInstance.data = event.payload.configuration ?? null;
        draft.apps.solutionInstance.originalData =
          event.payload.configuration ?? null;
        draft.apps.solutionInstance.error = null;
        draft.apps.salesforceData.ecaMigration.contextRefreshPending =
          draft.apps.salesforceData.ecaMigration.contextRefreshPending.filter(
            request => request !== 'solutionInstance',
          );
        break;
      }
      case 'apps - fetch solution instance failure': {
        draft.apps.solutionInstance.loading = false;
        draft.apps.solutionInstance.data = null;
        draft.apps.solutionInstance.error = event.payload.error;
        draft.apps.salesforceData.ecaMigration.contextRefreshFailed = true;
        draft.apps.salesforceData.ecaMigration.contextRefreshPending = [];
        break;
      }
      case 'apps - update configuration': {
        draft.apps.solutionInstance.data = event.payload.configuration;
        break;
      }
      case 'apps - fetch programs loading': {
        draft.apps.marketoData.programs.loading = true;
        draft.apps.marketoData.programs.error = null;
        break;
      }
      case 'apps - fetch programs success': {
        draft.apps.marketoData.programs.loading = false;
        draft.apps.marketoData.programs.items = event.payload.programs ?? [];
        draft.apps.marketoData.programs.error = null;
        break;
      }
      case 'apps - fetch programs failure': {
        draft.apps.marketoData.programs.loading = false;
        draft.apps.marketoData.programs.items = [];
        draft.apps.marketoData.programs.error = event.payload.error;
        break;
      }
      case 'apps - fetch folders loading': {
        draft.apps.marketoData.folders.loading = true;
        draft.apps.marketoData.folders.error = null;
        break;
      }
      case 'apps - fetch folders success': {
        draft.apps.marketoData.folders.loading = false;
        draft.apps.marketoData.folders.items = event.payload.folders ?? [];
        draft.apps.marketoData.folders.error = null;
        break;
      }
      case 'apps - fetch folders failure': {
        draft.apps.marketoData.folders.loading = false;
        draft.apps.marketoData.folders.items = [];
        draft.apps.marketoData.folders.error = event.payload.error;
        break;
      }
      case 'apps - fetch channels loading': {
        draft.apps.marketoData.channels.loading = true;
        draft.apps.marketoData.channels.error = null;
        break;
      }
      case 'apps - fetch channels success': {
        draft.apps.marketoData.channels.loading = false;
        draft.apps.marketoData.channels.items = event.payload.channels ?? [];
        draft.apps.marketoData.channels.error = null;
        break;
      }
      case 'apps - fetch channels failure': {
        draft.apps.marketoData.channels.loading = false;
        draft.apps.marketoData.channels.items = [];
        draft.apps.marketoData.channels.error = event.payload.error;
        break;
      }
      case 'apps - fetch program member fields loading': {
        draft.apps.marketoData.programMemberFields.loading = true;
        draft.apps.marketoData.programMemberFields.error = null;
        break;
      }
      case 'apps - fetch program member fields success': {
        draft.apps.marketoData.programMemberFields.loading = false;
        draft.apps.marketoData.programMemberFields.items =
          event.payload.programMemberFields ?? [];
        draft.apps.marketoData.programMemberFields.error = null;
        break;
      }
      case 'apps - fetch program member fields failure': {
        draft.apps.marketoData.programMemberFields.loading = false;
        draft.apps.marketoData.programMemberFields.items = [];
        draft.apps.marketoData.programMemberFields.error = event.payload.error;
        break;
      }
      case 'apps - fetch activity types loading': {
        draft.apps.marketoData.customActivities.loading = true;
        draft.apps.marketoData.customActivities.error = null;
        break;
      }
      case 'apps - fetch activity types success': {
        draft.apps.marketoData.customActivities.loading = false;
        draft.apps.marketoData.customActivities.items =
          event.payload.customActivities ?? [];
        draft.apps.marketoData.customActivities.error = null;
        break;
      }
      case 'apps - fetch activity types failure': {
        draft.apps.marketoData.customActivities.loading = false;
        draft.apps.marketoData.customActivities.items = [];
        draft.apps.marketoData.customActivities.error = event.payload.error;
        break;
      }
      case 'apps - fetch bank questions loading': {
        draft.apps.bankQuestions.loading = true;
        draft.apps.bankQuestions.error = null;
        break;
      }
      case 'apps - fetch bank questions success': {
        draft.apps.bankQuestions.loading = false;
        draft.apps.bankQuestions.items = event.payload.bankQuestions ?? [];
        draft.apps.bankQuestions.error = null;
        break;
      }
      case 'apps - fetch bank questions failure': {
        // Bank questions are supplementary (used for registration field mapping)
        // and must not block Salesforce ECA reconnection; only the local
        // bankQuestions state reflects the failure.
        draft.apps.bankQuestions.loading = false;
        draft.apps.bankQuestions.items = [];
        draft.apps.bankQuestions.error = event.payload.error;
        break;
      }
      case 'apps - fetch program member fields reg loading': {
        draft.apps.marketoData.programMemberFieldsReg.loading = true;
        draft.apps.marketoData.programMemberFieldsReg.error = null;
        break;
      }
      case 'apps - fetch program member fields reg success': {
        draft.apps.marketoData.programMemberFieldsReg.loading = false;
        draft.apps.marketoData.programMemberFieldsReg.items =
          event.payload.programMemberFieldsReg ?? [];
        draft.apps.marketoData.programMemberFieldsReg.error = null;
        break;
      }
      case 'apps - fetch program member fields reg failure': {
        draft.apps.marketoData.programMemberFieldsReg.loading = false;
        draft.apps.marketoData.programMemberFieldsReg.items = [];
        draft.apps.marketoData.programMemberFieldsReg.error =
          event.payload.error;
        break;
      }
      case 'apps - fetch custom activity questions loading': {
        draft.apps.marketoData.customActivityQuestions.loading = true;
        draft.apps.marketoData.customActivityQuestions.error = null;
        break;
      }
      case 'apps - clear custom activity questions': {
        draft.apps.marketoData.customActivityQuestions.data = {};
        break;
      }
      case 'apps - fetch custom activity questions success': {
        const key = event.payload.customActivityQuestions?.id || 0;
        draft.apps.marketoData.customActivityQuestions.loading = false;
        draft.apps.marketoData.customActivityQuestions.data[key] =
          event.payload.customActivityQuestions?.attributes || [];
        draft.apps.marketoData.customActivityQuestions.error = null;
        break;
      }
      case 'apps - fetch custom activity questions failure': {
        draft.apps.marketoData.customActivityQuestions.loading = false;
        draft.apps.marketoData.customActivityQuestions.data = {};
        draft.apps.marketoData.customActivityQuestions.error =
          event.payload.error;
        break;
      }
      case 'apps - fetch lead fields loading': {
        draft.apps.marketoData.leadFields.loading = true;
        draft.apps.marketoData.leadFields.error = null;
        break;
      }
      case 'apps - fetch lead fields success': {
        draft.apps.marketoData.leadFields.loading = false;
        draft.apps.marketoData.leadFields.items =
          event.payload.leadFields ?? [];
        draft.apps.marketoData.leadFields.error = null;
        break;
      }
      case 'apps - fetch lead fields failure': {
        draft.apps.marketoData.leadFields.loading = false;
        draft.apps.marketoData.leadFields.items = [];
        draft.apps.marketoData.leadFields.error = event.payload.error;
        break;
      }
      case 'apps - fetch external custom activity fields loading': {
        const activityType = event.payload.activityType;
        draft.apps.marketoData.customActivityFields[activityType].loading =
          true;
        draft.apps.marketoData.customActivityFields[activityType].error = null;
        break;
      }
      case 'apps - fetch external custom activity fields success': {
        const activityType = event.payload.activityType;
        draft.apps.marketoData.customActivityFields[activityType].loading =
          false;
        draft.apps.marketoData.customActivityFields[activityType].items =
          event.payload.items ?? [];
        draft.apps.marketoData.customActivityFields[activityType].error = null;
        break;
      }
      case 'apps - fetch external custom activity fields failure': {
        const activityType = event.payload.activityType;
        draft.apps.marketoData.customActivityFields[activityType].loading =
          false;
        draft.apps.marketoData.customActivityFields[activityType].items = [];
        draft.apps.marketoData.customActivityFields[activityType].error =
          event.payload.error;
        break;
      }
      case 'apps - update ui state': {
        draft.apps.marketoData.uiState = event.payload.uiState;
        break;
      }
      case 'apps - update salesforce ui state': {
        draft.apps.salesforceData.uiState = {
          ...draft.apps.salesforceData.uiState,
          ...event.payload.uiState,
        };
        break;
      }
      case 'apps - reset salesforce data': {
        const ecaMigration = draft.apps.salesforceData.ecaMigration;
        draft.apps.salesforceData = {
          sobjects: {
            items: [],
            loading: false,
            error: null,
          },
          sobjectFields: {
            data: {},
            loadingStates: {},
            errors: {},
          },
          campaigns: {
            items: [],
            loading: false,
            error: null,
          },
          customActivityFields: {
            data: {} as Record<string, SObjectFields[]>,
            external_salesforce_attendee_event_area_activity_fields: {
              loading: false,
              error: null,
            },
            external_salesforce_visited_replay_area_activity_fields: {
              loading: false,
              error: null,
            },
            external_salesforce_watched_replay_video_activity_fields: {
              loading: false,
              error: null,
            },
          },
          uiState: draft.apps.salesforceData.uiState,
          ecaMigration,
        };
        break;
      }
      case '[content] authentication started': {
        draft.contentHub.authentication.status = 'processing';
        break;
      }
      case '[content] authentication success': {
        draft.contentHub.authentication.status = 'success';
        draft.contentHub.authentication.contentApiAuthToken =
          event.payload.token;
        draft.contentHub.authentication.contentApiEndpoint =
          event.payload.endpoints.content_api;
        break;
      }
      case '[content] authentication failed': {
        draft.contentHub.authentication.status = 'failed';
        break;
      }
      case '[content] library reset': {
        draft.contentHub.libraryInfo = defaultContentHubState.libraryInfo;
        break;
      }
      case '[content] pages reset': {
        draft.contentHub.pageInfo = defaultContentHubState.pageInfo;
        break;
      }
      case '[content] update search text': {
        draft.contentHub.pageInfo.searchText = event.payload.searchText;
        draft.contentHub.pageInfo.currentPage = 1;
        break;
      }
      case '[content] clear search text': {
        draft.contentHub.pageInfo.searchText = '';
        break;
      }
      case '[content] pages fetch started': {
        draft.contentHub.pageInfo.pages.status = 'processing';
        break;
      }
      case '[content] pages fetch success': {
        const { data, ...pagination } = event.payload;

        draft.contentHub.pageInfo.pages = {
          status: 'success',
          data: {
            ...draft.contentHub.pageInfo.pages.data,
            [pagination.page]: data,
          },
          pagination: pagination,
        };
        break;
      }
      case '[content] hubs page loaded': {
        draft.contentHub.pageInfo.isPagesLoaded = true;
        break;
      }
      case '[content] page delete started': {
        draft.contentHub.pageInfo.pageDeletionInfo = {
          ...draft.contentHub.pageInfo.pageDeletionInfo,
          isDialogOpen: true,
          pageExternalId: event.payload.pageExternalId,
          status: 'processing',
        };
        break;
      }
      case '[content] page delete success': {
        draft.contentHub.pageInfo.pageDeletionInfo = {
          isDialogOpen: false,
          status: 'success',
        };
        break;
      }
      case '[content] page delete failed': {
        if (draft.contentHub.pageInfo.pageDeletionInfo) {
          draft.contentHub.pageInfo.pageDeletionInfo = {
            ...draft.contentHub.pageInfo.pageDeletionInfo,
            status: 'failed',
            errors: {},
          };
        }
        break;
      }
      case '[content] set page delete confirmation': {
        draft.contentHub.pageInfo.pageDeletionInfo = {
          isDialogOpen: true,
          pageExternalId: event.payload.pageExternalId,
          pageTitle: event.payload.pageTitle,
          status: 'idle',
        };
        break;
      }
      case '[content] page delete cancelled': {
        draft.contentHub.pageInfo.pageDeletionInfo = {
          isDialogOpen: false,
          status: 'idle',
        };
        break;
      }
      case '[content] duplicate page clicked': {
        draft.contentHub.pageInfo.pageDuplication = {
          pageExternalId: event.payload.pageExternalId,
          status: 'idle',
        };
        break;
      }
      case '[content] page duplication started': {
        if (!draft.contentHub.pageInfo.pageDuplication) break;

        draft.contentHub.pageInfo.pageDuplication.status = 'processing';
        break;
      }
      case '[content] page duplication success': {
        draft.contentHub.pageInfo.pageDuplication = undefined;
        break;
      }
      case '[content] page duplication failed': {
        draft.contentHub.pageInfo.pageDuplication = {
          status: 'failed',
          errors: event.payload.error,
        };
        break;
      }
      case '[content] page change clicked': {
        draft.contentHub.pageInfo.currentPage = event.payload.page;
        break;
      }
      case '[content] create page clicked': {
        const organizationId = draft.organizationExternalId;
        if (organizationId) {
          window.open(
            `${window.location.origin}/org/${organizationId}/page/create`,
            '_blank',
          );
        }
        break;
      }
      case '[content] update library search text': {
        draft.contentHub.libraryInfo.isLibraryLoaded = false;
        draft.contentHub.libraryInfo.recordings.pagination.next_cursor = null;
        draft.contentHub.libraryInfo.filters.searchText =
          event.payload.searchText;
        break;
      }
      case '[content] clear library search text': {
        draft.contentHub.libraryInfo.isLibraryLoaded = false;
        draft.contentHub.libraryInfo.recordings.pagination.next_cursor = null;
        draft.contentHub.libraryInfo.filters.searchText = '';
        break;
      }
      case '[content] update library filter': {
        draft.contentHub.libraryInfo.isLibraryLoaded = false;
        draft.contentHub.libraryInfo.recordings.pagination.next_cursor = null;
        const { event: eventId, ...filters } = event.payload;

        draft.contentHub.libraryInfo.filters = {
          ...draft.contentHub.libraryInfo.filters,
          ...filters,
        };

        if (eventId !== undefined) {
          draft.contentHub.libraryInfo.filters.events.selectedEvent =
            eventId || undefined;
        }

        if (filters.videoType === 'library-uploads') {
          draft.contentHub.libraryInfo.filters.events.selectedEvent = undefined;
          draft.contentHub.libraryInfo.filters.eventAreas = [];
        }

        break;
      }
      case '[content] events filter query reset': {
        draft.contentHub.libraryInfo.filters.events.options = [];
        draft.contentHub.libraryInfo.filters.events.endCursor = null;
        draft.contentHub.libraryInfo.filters.events.hasMoreOptions = false;
        break;
      }
      case '[content] update events filter query': {
        draft.contentHub.libraryInfo.filters.events.searchText =
          event.payload.query;
        break;
      }
      case 'apps - fetch salesforce sobjects loading': {
        draft.apps.salesforceData.sobjects.loading = true;
        draft.apps.salesforceData.sobjects.error = null;
        break;
      }
      case 'apps - fetch salesforce sobjects success': {
        draft.apps.salesforceData.sobjects.loading = false;
        draft.apps.salesforceData.sobjects.items = event.payload.sobjects ?? [];
        draft.apps.salesforceData.sobjects.error = null;
        draft.apps.salesforceData.ecaMigration.connectorRefreshPending =
          draft.apps.salesforceData.ecaMigration.connectorRefreshPending.filter(
            request => request !== 'sobjects',
          );
        break;
      }
      case 'apps - fetch salesforce sobjects failure': {
        draft.apps.salesforceData.sobjects.loading = false;
        draft.apps.salesforceData.sobjects.items = [];
        draft.apps.salesforceData.sobjects.error = event.payload.error;
        draft.apps.salesforceData.ecaMigration.connectorRefreshFailed = true;
        draft.apps.salesforceData.ecaMigration.connectorRefreshPending =
          draft.apps.salesforceData.ecaMigration.connectorRefreshPending.filter(
            request => request !== 'sobjects',
          );
        break;
      }
      case 'apps - fetch salesforce sobject fields loading': {
        if (
          event.payload.connectorRefreshGeneration !==
          draft.apps.salesforceData.ecaMigration.connectorRefreshGeneration
        ) {
          break;
        }
        const request = `sobjectFields:${event.payload.sobjectName}`;
        draft.apps.salesforceData.sobjectFields.loadingStates[
          event.payload.sobjectName
        ] = true;
        draft.apps.salesforceData.sobjectFields.errors[
          event.payload.sobjectName
        ] = null;
        if (
          draft.apps.salesforceData.ecaMigration.finalizationPhase ===
            'loading_connector_data' &&
          !draft.apps.salesforceData.ecaMigration.connectorRefreshPending.includes(
            request,
          )
        ) {
          draft.apps.salesforceData.ecaMigration.connectorRefreshPending.push(
            request,
          );
        }
        break;
      }
      case 'apps - fetch salesforce sobject fields success': {
        if (
          event.payload.connectorRefreshGeneration !==
          draft.apps.salesforceData.ecaMigration.connectorRefreshGeneration
        ) {
          break;
        }
        const request = `sobjectFields:${event.payload.sobjectName}`;
        draft.apps.salesforceData.sobjectFields.loadingStates[
          event.payload.sobjectName
        ] = false;
        draft.apps.salesforceData.sobjectFields.data[
          event.payload.sobjectName
        ] = event.payload.sobjectFields;
        draft.apps.salesforceData.sobjectFields.errors[
          event.payload.sobjectName
        ] = null;
        draft.apps.salesforceData.ecaMigration.connectorRefreshPending =
          draft.apps.salesforceData.ecaMigration.connectorRefreshPending.filter(
            pendingRequest => pendingRequest !== request,
          );
        break;
      }
      case 'apps - fetch salesforce sobject fields failure': {
        if (
          event.payload.connectorRefreshGeneration !==
          draft.apps.salesforceData.ecaMigration.connectorRefreshGeneration
        ) {
          break;
        }
        const request = `sobjectFields:${event.payload.sobjectName}`;
        draft.apps.salesforceData.sobjectFields.loadingStates[
          event.payload.sobjectName
        ] = false;
        draft.apps.salesforceData.sobjectFields.errors[
          event.payload.sobjectName
        ] = event.payload.error;
        if (
          draft.apps.salesforceData.ecaMigration.connectorRefreshPending.includes(
            request,
          )
        ) {
          draft.apps.salesforceData.ecaMigration.connectorRefreshFailed = true;
        }
        draft.apps.salesforceData.ecaMigration.connectorRefreshPending =
          draft.apps.salesforceData.ecaMigration.connectorRefreshPending.filter(
            pendingRequest => pendingRequest !== request,
          );
        break;
      }
      case 'apps - fetch salesforce campaigns loading': {
        draft.apps.salesforceData.campaigns.loading = true;
        draft.apps.salesforceData.campaigns.error = null;
        break;
      }
      case 'apps - fetch salesforce campaigns success': {
        draft.apps.salesforceData.campaigns.loading = false;
        draft.apps.salesforceData.campaigns.items =
          event.payload.campaigns ?? [];
        draft.apps.salesforceData.campaigns.error = null;
        draft.apps.salesforceData.ecaMigration.connectorRefreshPending =
          draft.apps.salesforceData.ecaMigration.connectorRefreshPending.filter(
            request => request !== 'campaigns',
          );
        break;
      }
      case 'apps - fetch salesforce campaigns failure': {
        draft.apps.salesforceData.campaigns.loading = false;
        draft.apps.salesforceData.campaigns.items = [];
        draft.apps.salesforceData.campaigns.error = event.payload.error;
        draft.apps.salesforceData.ecaMigration.connectorRefreshFailed = true;
        draft.apps.salesforceData.ecaMigration.connectorRefreshPending =
          draft.apps.salesforceData.ecaMigration.connectorRefreshPending.filter(
            request => request !== 'campaigns',
          );
        break;
      }
      case 'apps - fetch salesforce custom activity fields loading': {
        if (
          event.payload.connectorRefreshGeneration !==
          draft.apps.salesforceData.ecaMigration.connectorRefreshGeneration
        ) {
          break;
        }
        const activityType = event.payload.activityType;
        const request = `customActivityFields:${activityType}`;
        draft.apps.salesforceData.customActivityFields[activityType].loading =
          true;
        draft.apps.salesforceData.customActivityFields[activityType].error =
          null;
        if (
          draft.apps.salesforceData.ecaMigration.finalizationPhase ===
            'loading_connector_data' &&
          !draft.apps.salesforceData.ecaMigration.connectorRefreshPending.includes(
            request,
          )
        ) {
          draft.apps.salesforceData.ecaMigration.connectorRefreshPending.push(
            request,
          );
        }
        break;
      }
      case 'apps - fetch salesforce custom activity fields success': {
        if (
          event.payload.connectorRefreshGeneration !==
          draft.apps.salesforceData.ecaMigration.connectorRefreshGeneration
        ) {
          break;
        }
        const activityType = event.payload.activityType;
        const request = `customActivityFields:${activityType}`;
        draft.apps.salesforceData.customActivityFields[activityType].loading =
          false;
        draft.apps.salesforceData.customActivityFields.data[
          event.payload.sobjectName
        ] = event.payload.sobjectFields ?? [];
        draft.apps.salesforceData.customActivityFields[activityType].error =
          null;
        draft.apps.salesforceData.ecaMigration.connectorRefreshPending =
          draft.apps.salesforceData.ecaMigration.connectorRefreshPending.filter(
            pendingRequest => pendingRequest !== request,
          );
        break;
      }
      case 'apps - fetch salesforce custom activity fields failure': {
        if (
          event.payload.connectorRefreshGeneration !==
          draft.apps.salesforceData.ecaMigration.connectorRefreshGeneration
        ) {
          break;
        }
        const activityType = event.payload.activityType;
        const request = `customActivityFields:${activityType}`;
        draft.apps.salesforceData.customActivityFields[activityType].loading =
          false;
        draft.apps.salesforceData.customActivityFields[activityType].error =
          event.payload.error;
        if (
          draft.apps.salesforceData.ecaMigration.connectorRefreshPending.includes(
            request,
          )
        ) {
          draft.apps.salesforceData.ecaMigration.connectorRefreshFailed = true;
        }
        draft.apps.salesforceData.ecaMigration.connectorRefreshPending =
          draft.apps.salesforceData.ecaMigration.connectorRefreshPending.filter(
            pendingRequest => pendingRequest !== request,
          );
        break;
      }
      case '[content] recording fetch': {
        draft.contentHub.libraryInfo.isLibraryLoaded = false;
        draft.contentHub.libraryInfo.recordings.pagination.next_cursor = null;
        break;
      }
      case '[content] recording fetch started': {
        draft.contentHub.libraryInfo.recordings.status = 'processing';
        break;
      }
      case '[content] recording fetch success': {
        draft.contentHub.libraryInfo.isLibraryLoaded = true;
        draft.contentHub.libraryInfo.recordings = {
          data: !draft.contentHub.libraryInfo.recordings?.pagination
            ?.next_cursor
            ? event.payload.data
            : [
                ...(draft.contentHub.libraryInfo.recordings?.data ?? []),
                ...event.payload.data,
              ],
          pagination: event.payload.pagination,
          status: 'success',
        };
        break;
      }
      case '[content] recording fetch failed': {
        draft.contentHub.libraryInfo.isLibraryLoaded = true;
        draft.contentHub.libraryInfo.recordings = {
          data: draft.contentHub.libraryInfo.recordings?.data ?? [],
          pagination: {
            next_cursor:
              draft.contentHub.libraryInfo.recordings?.pagination?.next_cursor,
          },
          status: 'failed',
          errors: event.payload.error,
        };
        break;
      }
      case '[content] recording delete started': {
        draft.contentHub.libraryInfo.recordingDeletionInfo = {
          ...draft.contentHub.libraryInfo.recordingDeletionInfo,
          isDialogOpen: true,
          recordingId: event.payload.recordingId,
          status: 'processing',
        };
        break;
      }
      case '[content] recording delete success': {
        // Filter out the deleted recording from the existing list
        const recordingIdToDelete =
          draft.contentHub.libraryInfo.recordingDeletionInfo.recordingId;
        if (recordingIdToDelete) {
          draft.contentHub.libraryInfo.recordings.data =
            draft.contentHub.libraryInfo.recordings.data.filter(
              recording => recording.id !== recordingIdToDelete,
            );
        }
        draft.contentHub.libraryInfo.recordingDeletionInfo = {
          isDialogOpen: false,
          status: 'success',
          hasSimulive: false,
          hasUpcomingStudios: false,
        };
        break;
      }
      case '[content] recording delete failed': {
        if (draft.contentHub.libraryInfo.recordingDeletionInfo) {
          draft.contentHub.libraryInfo.recordingDeletionInfo = {
            ...draft.contentHub.libraryInfo.recordingDeletionInfo,
            status: 'failed',
            errors: event.payload.error,
          };
        }
        break;
      }
      case '[content] show recording delete dialog': {
        draft.contentHub.libraryInfo.recordingDeletionInfo = {
          isDialogOpen: true,
          recordingId: event.payload.recordingId,
          recordingTitle: event.payload.recordingTitle,
          status: 'idle',
          hasSimulive: event.payload.hasSimulive,
          hasUpcomingStudios: event.payload.hasUpcomingStudios,
          eventPartType: event.payload.eventPartType,
        };
        break;
      }
      case '[content] recording delete cancelled': {
        draft.contentHub.libraryInfo.recordingDeletionInfo = {
          isDialogOpen: false,
          status: 'idle',
          hasSimulive: false,
          hasUpcomingStudios: false,
        };
        break;
      }
      case '[content] recording rename started': {
        draft.contentHub.libraryInfo.recordingRenameInfo = {
          ...draft.contentHub.libraryInfo.recordingRenameInfo,
          isDialogOpen: true,
          recordingId: event.payload.recordingId,
          newTitle: event.payload.newTitle,
          status: 'processing',
        };
        break;
      }
      case '[content] recording rename success': {
        // Find and update the recording in the list with the new title
        const { recordingId, newTitle } =
          draft.contentHub.libraryInfo.recordingRenameInfo;
        if (recordingId && newTitle) {
          const recording = draft.contentHub.libraryInfo.recordings.data.find(
            r => r.id === recordingId,
          );
          if (recording) {
            recording.title = newTitle;
          }
        }

        draft.contentHub.libraryInfo.recordingRenameInfo = {
          isDialogOpen: false,
          status: 'success',
        };
        break;
      }
      case '[content] recording rename failed': {
        if (draft.contentHub.libraryInfo.recordingRenameInfo) {
          draft.contentHub.libraryInfo.recordingRenameInfo = {
            ...draft.contentHub.libraryInfo.recordingRenameInfo,
            status: 'failed',
            errors: event.payload.error,
          };
        }
        break;
      }
      case '[content] show recording rename dialog': {
        draft.contentHub.libraryInfo.recordingRenameInfo = {
          isDialogOpen: true,
          recordingId: event.payload.recordingId,
          recordingTitle: event.payload.recordingTitle,
          status: 'idle',
        };
        break;
      }
      case '[content] recording rename cancelled': {
        draft.contentHub.libraryInfo.recordingRenameInfo = {
          isDialogOpen: false,
          status: 'idle',
        };
        break;
      }
      case '[content] publish-to-events list loading': {
        draft.contentHub.recordingInfo.publishToEvents.status = 'processing';
        break;
      }
      case '[content] publish-to-events list loaded': {
        draft.contentHub.recordingInfo.publishToEvents.status = 'success';
        draft.contentHub.recordingInfo.publishToEvents.eventsList =
          event.payload.data;
        draft.contentHub.recordingInfo.publishToEvents.pageInfo =
          event.payload.pageInfo;
        break;
      }
      case '[content] publish-to-events list failed': {
        draft.contentHub.recordingInfo.publishToEvents.status = 'failed';
        break;
      }
      case '[content] publish-to-events assignments loading': {
        draft.contentHub.recordingInfo.publishToEvents.assignmentsStatus =
          'processing';
        break;
      }
      case '[content] publish-to-events assignments loaded': {
        draft.contentHub.recordingInfo.publishToEvents.assignmentsStatus =
          'success';
        draft.contentHub.recordingInfo.publishToEvents.initialSelectedEvents = {
          ...event.payload.selectedEvents,
        };
        draft.contentHub.recordingInfo.publishToEvents.draftSelectedEvents = {
          ...event.payload.selectedEvents,
        };
        draft.contentHub.recordingInfo.publishToEvents.eventAreaValidationErrors =
          {};
        break;
      }
      case '[content] publish-to-events assignments failed': {
        // TODO: handle error state
        // JIRA: https://jira.ringcentral.com/browse/EVE-15224
        draft.contentHub.recordingInfo.publishToEvents.assignmentsStatus =
          'failed';
        break;
      }
      case '[content] publish-to-events video areas loading': {
        const publishToEvents = draft.contentHub.recordingInfo.publishToEvents;
        publishToEvents.videoAreas.byEventId[event.payload.eventId] = {
          data: [],
          status: 'processing',
        };
        break;
      }
      case '[content] publish-to-events video areas loaded': {
        const publishToEvents = draft.contentHub.recordingInfo.publishToEvents;
        publishToEvents.videoAreas.byEventId[event.payload.eventId] = {
          data: event.payload.videoAreas,
          status: 'success',
        };

        if (
          publishToEvents.draftSelectedEvents[event.payload.eventId] === null &&
          event.payload.videoAreas.length === 1
        ) {
          const [videoArea] = event.payload.videoAreas;

          publishToEvents.draftSelectedEvents[event.payload.eventId] = {
            eventPartId: videoArea.id,
            eventPartType: videoArea.type,
            eventPartName: videoArea.name,
          };
          delete publishToEvents.eventAreaValidationErrors[
            event.payload.eventId
          ];
        }
        break;
      }
      case '[content] publish-to-events video areas failed': {
        const publishToEvents = draft.contentHub.recordingInfo.publishToEvents;
        publishToEvents.videoAreas.byEventId[event.payload.eventId] = {
          data: [],
          status: 'failed',
          errors: {
            message: event.payload.error.message,
          },
        };
        break;
      }
      case '[content] publish-to-events search text changed': {
        draft.contentHub.recordingInfo.publishToEvents.status = 'processing';
        draft.contentHub.recordingInfo.publishToEvents.searchText =
          event.payload.searchText;
        draft.contentHub.recordingInfo.publishToEvents.eventsList = [];
        draft.contentHub.recordingInfo.publishToEvents.pageInfo = {
          endCursor: null,
          hasNextPage: false,
        };
        draft.contentHub.recordingInfo.publishToEvents.videoAreas = {
          byEventId: {},
        };
        break;
      }
      case '[content] publish-to-events event selection changed': {
        const publishToEvents = draft.contentHub.recordingInfo.publishToEvents;
        const { eventId } = event.payload;
        const draftSelectedEvents = publishToEvents.draftSelectedEvents;

        delete publishToEvents.eventAreaValidationErrors[eventId];

        if (draftSelectedEvents[eventId] !== undefined) {
          delete draftSelectedEvents[eventId];
        } else {
          const loadedVideoAreas =
            publishToEvents.videoAreas.byEventId[eventId]?.data;
          const [singleVideoArea] = loadedVideoAreas ?? [];

          draftSelectedEvents[eventId] =
            loadedVideoAreas?.length === 1
              ? {
                  eventPartId: singleVideoArea.id,
                  eventPartType: singleVideoArea.type,
                  eventPartName: singleVideoArea.name,
                }
              : null;
        }
        break;
      }
      case '[content] publish-to-events event area changed': {
        const publishToEvents = draft.contentHub.recordingInfo.publishToEvents;
        publishToEvents.draftSelectedEvents[event.payload.eventId] =
          event.payload.eventPart;
        delete publishToEvents.eventAreaValidationErrors[event.payload.eventId];
        break;
      }
      case '[content] publish-to-events validation failed': {
        const publishToEvents = draft.contentHub.recordingInfo.publishToEvents;
        publishToEvents.eventAreaValidationErrors =
          event.payload.eventIds.reduce<Record<string, boolean>>(
            (acc, eventId) => {
              acc[eventId] = true;
              return acc;
            },
            {},
          );
        break;
      }
      case '[content] publish-to-events back action clicked': {
        draft.contentHub.recordingInfo.publishToEvents.searchText = '';
        draft.contentHub.recordingInfo.publishToEvents.eventsList = [];
        draft.contentHub.recordingInfo.publishToEvents.pageInfo = {
          hasNextPage: false,
          endCursor: null,
        };
        draft.contentHub.recordingInfo.publishToEvents.status = 'idle';
        draft.contentHub.recordingInfo.publishToEvents.draftSelectedEvents = {
          ...draft.contentHub.recordingInfo.publishToEvents
            .initialSelectedEvents,
        };
        draft.contentHub.recordingInfo.publishToEvents.eventAreaValidationErrors =
          {};
        draft.contentHub.recordingInfo.publishToEvents.videoAreas = {
          byEventId: {},
        };
        break;
      }
      case '[content] publish-to-events state reset': {
        draft.contentHub.recordingInfo.publishToEvents = {
          searchText: '',
          eventsList: [],
          pageInfo: { hasNextPage: false, endCursor: null },
          status: 'idle',
          videoAreas: {
            byEventId: {},
          },
          initialSelectedEvents: {},
          draftSelectedEvents: {},
          eventAreaValidationErrors: {},
          assignmentsStatus: 'idle',
        };
        break;
      }
      case '[content] org-level recording upload started': {
        draft.contentHub.orgLevelRecordingUpload = {
          isUploading: true,
          progress: 0,
          completed: false,
        };
        break;
      }
      case '[content] org-level recording upload progress': {
        draft.contentHub.orgLevelRecordingUpload.progress = Math.round(
          event.payload.value * 100,
        );
        break;
      }
      case '[content] org-level recording upload success': {
        draft.contentHub.orgLevelRecordingUpload = {
          isUploading: false,
          progress: 100,
          completed: true,
        };
        break;
      }
      case '[content] org-level recording upload reset': {
        draft.contentHub.orgLevelRecordingUpload = {
          isUploading: false,
          progress: 0,
          completed: false,
        };
        break;
      }
      case '[content] recording view fetch failed': {
        draft.contentHub.recordingInfo.recordingRedirectFailed = true;
        break;
      }
      case '[content] recording view loading started': {
        draft.contentHub.recordingInfo.recordingRedirectFailed = false;
        break;
      }
      case '[event-wizard] set experience type': {
        draft.eventWizard.formData.template = event.payload.template;
        break;
      }
      case '[event-wizard] set venue selection': {
        draft.eventWizard.venue = event.payload.venue;
        break;
      }
      case '[event-wizard] submit experience details form': {
        draft.eventWizard.formData.basicDetails.name = event.payload.name;
        draft.eventWizard.formData.basicDetails.timeStart =
          event.payload.timeStart;
        draft.eventWizard.formData.basicDetails.timeEnd = event.payload.timeEnd;
        break;
      }
      case '[event-wizard] set description': {
        draft.eventWizard.formData.description = event.payload;
        break;
      }
      case '[event-wizard] set organization': {
        draft.eventWizard.organization = event.payload;
        break;
      }
      case '[event-wizard] set event location name': {
        draft.eventWizard.formData.basicDetails.eventLocation.name =
          event.payload;
        break;
      }
      case '[event-wizard] reset event location name': {
        draft.eventWizard.formData.basicDetails.eventLocation.name = '';
        break;
      }
      case '[event-wizard] set event location address': {
        draft.eventWizard.formData.basicDetails.eventLocation.address =
          event.payload;
        break;
      }
      case '[event-wizard] reset event location address': {
        draft.eventWizard.formData.basicDetails.eventLocation.address =
          undefined;
        break;
      }
      case '[event-wizard] set timezone': {
        draft.eventWizard.formData.timezone = event.payload;
        break;
      }
      case '[event-wizard] reset event creation': {
        draft.eventWizard.eventCreation.status = 'idle';
        break;
      }
      case '[event-wizard] reset wizard': {
        draft.eventWizard = defaultEventWizardState;
        break;
      }
      case '[event-wizard] event creation started': {
        draft.eventWizard.eventCreation.status = 'processing';
        break;
      }
      case '[event-wizard] event creation success': {
        draft.eventWizard.eventCreation.status = 'success';
        break;
      }
      case '[event-wizard] event creation failed': {
        draft.eventWizard.eventCreation.status = 'failed';
        break;
      }
      case '[event-wizard] event series fetch started': {
        draft.eventWizard.eventSeries.status = 'loading';
        draft.eventWizard.eventSeries.error = null;
        break;
      }
      case '[event-wizard] event series fetch success': {
        draft.eventWizard.eventSeries.status = 'success';
        draft.eventWizard.eventSeries.data = event.payload.eventSeries;
        draft.eventWizard.eventSeries.pagination = event.payload.pagination;

        if (
          event.payload.eventSeries.length > 0 &&
          !draft.eventWizard.eventSeries.organizationHasSeries
        ) {
          draft.eventWizard.eventSeries.organizationHasSeries = true;
        }
        break;
      }
      case '[event-wizard] event series fetch failed': {
        draft.eventWizard.eventSeries.status = 'failed';
        draft.eventWizard.eventSeries.error = event.payload;
        break;
      }
      case '[event-wizard] event series update search query': {
        draft.eventWizard.eventSeries.query = event.payload.query;
        draft.eventWizard.eventSeries.pagination.endCursor = null;
        draft.eventWizard.eventSeries.status = 'idle';
        break;
      }
      case '[event-wizard] event series reset search query': {
        draft.eventWizard.eventSeries.query = '';
        draft.eventWizard.eventSeries.pagination.endCursor = null;
        draft.eventWizard.eventSeries.status = 'idle';
        break;
      }
      case '[event-wizard] event series set selected series': {
        draft.eventWizard.eventSeries.selected = event.payload.eventSeries;
        draft.eventWizard.eventSeries.pagination.endCursor = null;
        draft.eventWizard.eventSeries.query = '';
        draft.eventWizard.eventSeries.status = 'idle';
        draft.eventWizard.eventSeries.isSelectionEnabled = true;
        draft.eventWizard.eventSeries.organizationHasSeries = true;
        break;
      }

      case '[event-wizard] event series toggle selection': {
        const isSelectionEnabled =
          draft.eventWizard.eventSeries.isSelectionEnabled;

        draft.eventWizard.eventSeries.isSelectionEnabled = !isSelectionEnabled;
        draft.eventWizard.eventSeries.query = '';
        draft.eventWizard.eventSeries.status = 'idle';

        if (!isSelectionEnabled) {
          draft.eventWizard.eventSeries.selected = null;
        }
        break;
      }
      case '[content] events filter fetch started': {
        draft.contentHub.libraryInfo.filters.events.status = 'processing';
        break;
      }
      case '[content] events filter fetch success': {
        draft.contentHub.libraryInfo.filters.events.options = [
          ...draft.contentHub.libraryInfo.filters.events.options,
          ...event.payload.data,
        ];
        draft.contentHub.libraryInfo.filters.events.endCursor =
          event.payload.pageInfo.endCursor || null;
        draft.contentHub.libraryInfo.filters.events.hasMoreOptions =
          event.payload.pageInfo.hasNextPage;
        draft.contentHub.libraryInfo.filters.events.hasInitialised = true;
        draft.contentHub.libraryInfo.filters.events.status = 'success';
        break;
      }
      case '[content] events filter fetch failed': {
        draft.contentHub.libraryInfo.filters.events.status = 'failed';
        draft.contentHub.libraryInfo.filters.events.hasMoreOptions = false;
        draft.contentHub.libraryInfo.filters.events.endCursor = null;
        break;
      }
      case '[analytics] update recording search text': {
        draft.analytics.recordingInfo.isRecordingsLoaded = false;
        draft.analytics.recordingInfo.recordingsData.pagination.next_cursor =
          null;
        draft.analytics.recordingInfo.filters.searchText =
          event.payload.searchText;
        break;
      }
      case '[analytics] clear recording search text': {
        draft.analytics.recordingInfo.isRecordingsLoaded = false;
        draft.analytics.recordingInfo.recordingsData.pagination.next_cursor =
          null;
        draft.analytics.recordingInfo.filters.searchText = '';
        break;
      }
      case '[analytics] recording fetch started': {
        draft.analytics.recordingInfo.recordingsData.status = 'processing';
        break;
      }
      case '[analytics] recording fetch success': {
        draft.analytics.recordingInfo.isRecordingsLoaded = true;
        draft.analytics.recordingInfo.recordingsData = {
          data: !draft.analytics.recordingInfo.recordingsData?.pagination
            ?.next_cursor
            ? event.payload.data
            : [
                ...(draft.analytics.recordingInfo.recordingsData?.data ?? []),
                ...event.payload.data,
              ],
          pagination: event.payload.pagination,
          status: 'success',
        };
        break;
      }
      case '[analytics] recording fetch failed': {
        draft.analytics.recordingInfo.recordingsData = {
          data: [],
          pagination: {
            next_cursor: null,
          },
          status: 'failed',
          errors: event.payload.error,
        };
        break;
      }
      case '[analytics] recording reset': {
        draft.analytics = defaultAnalyticsState;
        break;
      }
      case '[analytics] series fetch started': {
        draft.analytics.seriesInfo.isLoading = true;
        break;
      }
      case '[analytics] series fetch success': {
        draft.analytics.seriesInfo.seriesData = {
          data: [...event.payload.data],
          pagination: {
            endCursor: event.payload.pageInfo.endCursor,
            hasNextPage: event.payload.pageInfo.hasNextPage,
          },
        };
        draft.analytics.seriesInfo.isLoading = false;
        break;
      }
      case '[analytics] series fetch failed': {
        draft.analytics.seriesInfo.isLoading = false;
        draft.analytics.seriesInfo.error = event.payload.error?.message;
        break;
      }
      case '[analytics] update series search text': {
        draft.analytics.seriesInfo.isLoading = true;
        draft.analytics.seriesInfo.filters.searchText =
          event.payload.searchText;
        draft.analytics.seriesInfo.seriesData.data = [];
        draft.analytics.seriesInfo.seriesData.pagination = {
          endCursor: null,
          hasNextPage: false,
        };
        break;
      }
      case '[analytics] update series sort key and direction': {
        draft.analytics.seriesInfo.sortConfig.name = event.payload.name;
        draft.analytics.seriesInfo.sortConfig.reverseSort =
          event.payload.reverseSort;
        draft.analytics.seriesInfo.seriesData.data = [];
        draft.analytics.seriesInfo.seriesData.pagination = {
          endCursor: null,
          hasNextPage: false,
        };
        break;
      }
      case '[organization] events fetch started': {
        draft.organization.events.status = 'processing';
        break;
      }
      case '[organization] events fetch success': {
        draft.organization.events.status = 'success';
        const newItems: string[] = [];
        const newOrganizationEvents: Record<string, Event> = {
          ...draft.organization.organizationEvents,
        };
        (event.payload.data ?? []).forEach(eventData => {
          newItems.push(eventData.id);
          newOrganizationEvents[eventData.id] = eventData;
        });
        draft.organization.events.items = Array.from(
          new Set(
            draft.organization.events.pageInfo.endCursor &&
            !draft.organization.events.loadFirstPage
              ? [...draft.organization.events.items, ...newItems]
              : newItems,
          ),
        );
        draft.organization.organizationEvents = newOrganizationEvents;
        draft.organization.events.pageInfo = event.payload.pageInfo;
        draft.organization.events.features = event.payload.features;
        draft.organization.events.loadFirstPage = false;
        break;
      }
      case '[organization] events fetch failed': {
        draft.organization.events.status = 'failed';
        draft.organization.events.error = event.payload.error;
        break;
      }
      case '[organization] update events search text': {
        draft.organization.events.status = 'processing';
        draft.organization.events.filters.searchText =
          event.payload.filters.searchText;
        draft.organization.events.pageInfo = {
          endCursor: null,
          hasNextPage: false,
        };
        break;
      }
      case '[organization] update events status filter': {
        draft.organization.events.status = 'processing';
        draft.organization.events.filters.eventStatus =
          event.payload.filters.eventStatus || [];
        draft.organization.events.pageInfo = {
          endCursor: null,
          hasNextPage: false,
        };
        break;
      }
      case '[organization] update events sort': {
        draft.organization.events.sort = {
          sortField: event.payload.sort.sortField as EventSortKeys,
          reverseSort: event.payload.sort.reverseSort,
        };
        draft.organization.events.pageInfo = {
          endCursor: null,
          hasNextPage: false,
        };
        break;
      }
      case '[organization] update organization event': {
        if (
          event.payload?.data?.id &&
          draft.organization.organizationEvents[event.payload.data.id]
        ) {
          draft.organization.organizationEvents[event.payload.data.id] = {
            ...draft.organization.organizationEvents[event.payload.data.id],
            ...event.payload.data,
          };
        }
        break;
      }
      case '[organization] update events load first page': {
        draft.organization.events.loadFirstPage = event.payload.loadFirstPage;
        break;
      }
      case '[organization] series fetch started': {
        draft.organization.series.status = 'processing';
        break;
      }
      case '[organization] series fetch success': {
        draft.organization.series.status = 'success';
        const newSeriesIds: string[] = [];
        const newOrganizationSeries: Record<string, EventSeries> = {
          ...draft.organization.organizationSeries,
        };
        (event.payload.data ?? []).forEach(eventData => {
          newSeriesIds.push(eventData.id);
          newOrganizationSeries[eventData.id] = eventData;
        });
        draft.organization.series.items = Array.from(
          new Set(
            draft.organization.series.pageInfo.endCursor &&
            !draft.organization.series.loadFirstPage
              ? [...draft.organization.series.items, ...newSeriesIds]
              : newSeriesIds,
          ),
        );
        draft.organization.organizationSeries = newOrganizationSeries;
        draft.organization.series.pageInfo = event.payload.pageInfo;
        draft.organization.series.loadFirstPage = false;
        break;
      }
      case '[organization] series fetch failed': {
        draft.organization.series.status = 'failed';
        draft.organization.series.error = event.payload.error;
        break;
      }
      case '[organization] update series search text': {
        draft.organization.series.status = 'processing';
        draft.organization.series.filters.searchText =
          event.payload.filters.searchText;
        draft.organization.series.pageInfo = {
          endCursor: null,
          hasNextPage: false,
        };
        break;
      }
      case '[organization] update series sort': {
        draft.organization.series.sort = {
          sortField: event.payload.sort.sortField as EventSeriesSortKeys,
          reverseSort: event.payload.sort.reverseSort,
        };
        draft.organization.series.pageInfo = {
          endCursor: null,
          hasNextPage: false,
        };
        break;
      }
      case '[organization] update series load first page': {
        draft.organization.series.loadFirstPage = event.payload.loadFirstPage;
        break;
      }
      case '[organization] series list unmounted': {
        draft.organization.series.loadFirstPage = true;
        draft.organization.series.expandedSeriesId = '';
        break;
      }
      case '[organization] fetch organization details started': {
        draft.organization.organizationInfo.status = 'processing';
        break;
      }
      case '[organization] fetch organization details success': {
        draft.organization.organizationInfo.status = 'success';
        draft.organization.organizationInfo.name = event.payload.name;
        draft.organization.organizationInfo.about = event.payload.about;
        draft.organization.organizationInfo.email = event.payload.email;
        draft.organization.organizationInfo.facebookUrl =
          event.payload.facebookUrl;
        draft.organization.organizationInfo.linkedinUrl =
          event.payload.linkedinUrl;
        draft.organization.organizationInfo.twitterUrl =
          event.payload.twitterUrl;
        draft.organization.organizationInfo.websiteUrl =
          event.payload.websiteUrl;
        draft.organization.organizationInfo.features = event.payload.features;
        draft.organization.organizationInfo.paymentPlan =
          event.payload.paymentPlan;
        draft.organization.organizationInfo.image = event.payload.image;
        draft.organization.organizationInfo.smallImage =
          event.payload.smallImage;
        draft.organization.organizationInfo.largeImage =
          event.payload.largeImage;
        draft.organization.organizationInfo.createdAt = event.payload.createdAt;
        draft.organization.organizationInfo.standaloneStudio =
          event.payload.standaloneStudio;
        draft.organization.organizationInfo.settings = event.payload.settings;
        draft.navBar.selectedOrgInfo = {
          name: event.payload.name,
          image: event.payload.image,
          paymentPlan: event.payload.paymentPlan?.planName || '',
          planNameLocale: event.payload.paymentPlan?.planNameLocale || '',
          largeImage: event.payload.largeImage,
        };
        break;
      }
      case '[organization] fetch organization details failed': {
        draft.organization.organizationInfo.status = 'failed';
        draft.organization.organizationInfo.error = event.payload.error;
        draft.navBar.isOrgSelected = false;
        break;
      }
      case '[organization] series events fetch': {
        draft.organization.series.expandedSeriesId =
          event.payload.eventSeriesId;
        break;
      }
      case '[organization] series events fetch started': {
        draft.organization.seriesEvents[event.payload.eventSeriesId] = {
          ...draft.organization.seriesEvents[event.payload.eventSeriesId],
          status: 'processing',
          items: [],
        };
        break;
      }
      case '[organization] series events fetch success': {
        draft.organization.seriesEvents[event.payload.eventSeriesId] = {
          status: 'success',
          items: event.payload.data ?? [],
        };
        break;
      }
      case '[organization] series events fetch failed': {
        draft.organization.seriesEvents[event.payload.eventSeriesId] = {
          status: 'failed',
          errors: event.payload.error,
          items: [],
        };
        break;
      }
      case '[organization] update organization series': {
        if (
          event.payload?.data?.id &&
          draft.organization.organizationSeries[event.payload.data.id]
        ) {
          draft.organization.organizationSeries[event.payload.data.id] = {
            ...draft.organization.organizationSeries[event.payload.data.id],
            ...event.payload.data,
          };
        }
        break;
      }
      case '[organization] bulk update organization series': {
        event.payload.data.forEach(series => {
          if (series.id && draft.organization.organizationSeries[series.id]) {
            draft.organization.organizationSeries[series.id] = {
              ...draft.organization.organizationSeries[series.id],
              ...series,
            };
          }
        });
        break;
      }
      case '[organization] bulk update organization series events related data': {
        event.payload.data.forEach(series => {
          if (series.id && draft.organization.organizationSeries[series.id]) {
            draft.organization.organizationSeries[series.id] = {
              ...draft.organization.organizationSeries[series.id],
              eventsCount:
                draft.organization.organizationSeries[series.id].eventsCount +
                series.eventsCount, // send minus if removing
              registrants:
                draft.organization.organizationSeries[series.id].registrants +
                series.registrants, // send minus if removing
            };
          }
        });
        break;
      }
      case '[organization] delete organization series': {
        if (event.payload?.eventSeriesId) {
          if (
            draft.organization.organizationSeries[event.payload.eventSeriesId]
          ) {
            delete draft.organization.organizationSeries[
              event.payload.eventSeriesId
            ];
          }
          draft.organization.series.items =
            draft.organization.series.items.filter(
              id => id !== event.payload.eventSeriesId,
            );
        }
        break;
      }
      case '[organization] delete organization event': {
        if (event.payload?.eventId) {
          if (draft.organization.organizationEvents[event.payload.eventId]) {
            delete draft.organization.organizationEvents[event.payload.eventId];
          }
          draft.organization.events.items =
            draft.organization.events.items.filter(
              id => id !== event.payload.eventId,
            );
        }
        break;
      }

      case '[organization] add events to series modal events fetch': {
        draft.organization.addEventsToSeries.pinSeries =
          event.payload.eventSeriesId;
        break;
      }
      case '[organization] add events to series modal events fetch started': {
        draft.organization.addEventsToSeries.status = 'processing';
        if (!draft.organization.addEventsToSeries.pageInfo.endCursor) {
          draft.organization.addEventsToSeries.events = [];
        }
        break;
      }
      case '[organization] add events to series modal events fetch success': {
        draft.organization.addEventsToSeries.events = event.payload.data ?? [];
        draft.organization.addEventsToSeries.pageInfo = event.payload.pageInfo;
        draft.organization.addEventsToSeries.status = 'success';
        draft.organization.addEventsToSeries.addedEventIds = (
          event.payload.data ?? []
        ).reduce((acc, event) => {
          const isInSeries = event.eventSeries.edges?.some(
            edge =>
              edge?.node?.id === draft.organization.addEventsToSeries.pinSeries,
          );
          if (isInSeries) {
            acc.push(event.id);
          }
          return acc;
        }, [] as string[]);
        break;
      }
      case '[organization] add events to series modal events fetch failed': {
        draft.organization.addEventsToSeries.status = 'failed';
        break;
      }
      case '[organization] update add events to series modal event search text': {
        draft.organization.addEventsToSeries.searchText =
          event.payload.searchText;
        draft.organization.addEventsToSeries.pageInfo = {
          hasNextPage: false,
          endCursor: null,
        };
        draft.organization.addEventsToSeries.status = 'processing';
        break;
      }
      case '[organization] reset add events to series modal event listing data': {
        draft.organization.addEventsToSeries = {
          events: [],
          pageInfo: {
            hasNextPage: false,
            endCursor: null,
          },
          status: 'idle',
          searchText: '',
          pinSeries: '',
          addedEventIds: [],
        };
        break;
      }
      case '[organization] create series modal events fetch started': {
        draft.organization.createSeriesModal.status = 'processing';
        if (!draft.organization.createSeriesModal.pageInfo.endCursor) {
          draft.organization.createSeriesModal.eventIds = [];
        }
        break;
      }
      case '[organization] create series modal events fetch success': {
        draft.organization.createSeriesModal.status = 'success';
        const newItems: string[] = [];
        const newOrganizationEvents: Record<string, Event> = {
          ...draft.organization.organizationEvents,
        };
        (event.payload.data ?? []).forEach(eventData => {
          newItems.push(eventData.id);
          newOrganizationEvents[eventData.id] = eventData;
        });
        draft.organization.createSeriesModal.eventIds = Array.from(
          new Set([
            ...draft.organization.createSeriesModal.eventIds,
            ...newItems,
          ]),
        );
        draft.organization.organizationEvents = newOrganizationEvents;
        draft.organization.createSeriesModal.pageInfo = event.payload.pageInfo;
        break;
      }
      case '[organization] create series modal events fetch failed': {
        draft.organization.createSeriesModal.status = 'failed';
        break;
      }
      case '[organization] update create series modal event search text': {
        draft.organization.createSeriesModal.eventSearchText =
          event.payload.searchText;
        draft.organization.createSeriesModal.pageInfo = {
          endCursor: null,
          hasNextPage: false,
        };
        draft.organization.createSeriesModal.status = 'processing';
        break;
      }
      case '[organization] reset create series modal event listing data': {
        draft.organization.createSeriesModal.eventSearchText = '';
        draft.organization.createSeriesModal.pageInfo = {
          endCursor: null,
          hasNextPage: false,
        };
        draft.organization.createSeriesModal.eventIds = [];
        break;
      }
      case '[organization] add series to event modal events fetch': {
        draft.organization.addSeriesToEvent.pinEvent = event.payload.eventId;
        break;
      }
      case '[organization] add series to event modal events fetch started': {
        draft.organization.addSeriesToEvent.status = 'processing';
        if (!draft.organization.addSeriesToEvent.pageInfo.endCursor) {
          draft.organization.addSeriesToEvent.seriesList = [];
        }
        break;
      }
      case '[organization] add series to event modal events fetch success': {
        draft.organization.addSeriesToEvent.seriesList =
          event.payload.data ?? [];
        draft.organization.addSeriesToEvent.pageInfo = event.payload.pageInfo;
        draft.organization.addSeriesToEvent.status = 'success';
        break;
      }
      case '[organization] add series to event modal events fetch failed': {
        draft.organization.addSeriesToEvent.status = 'failed';
        break;
      }
      case '[organization] update add series to event modal event search text': {
        draft.organization.addSeriesToEvent.searchText =
          event.payload.searchText;
        draft.organization.addSeriesToEvent.pageInfo = {
          endCursor: null,
          hasNextPage: false,
        };
        draft.organization.addSeriesToEvent.status = 'processing';
        break;
      }
      case '[organization] reset add series to event modal event listing data': {
        draft.organization.addSeriesToEvent = {
          seriesList: [],
          pageInfo: {
            endCursor: null,
            hasNextPage: false,
          },
          status: 'idle',
          searchText: '',
          pinEvent: '',
        };
        break;
      }
      case '[organization] post create series series fetch success': {
        draft.organization.series.items = [];
        break;
      }
      case '[organization] latest ended events fetch started': {
        draft.organization.organizationLatestEndedEvents.status = 'processing';
        break;
      }
      case '[organization] latest ended events fetch success': {
        draft.organization.organizationLatestEndedEvents.status = 'success';
        draft.organization.organizationLatestEndedEvents.items =
          event.payload.data.reduce((acc, eventId) => {
            acc[eventId] = true;
            return acc;
          }, {} as Record<string, boolean>);
        break;
      }
      case '[organization] latest ended events fetch failed': {
        draft.organization.organizationLatestEndedEvents.status = 'failed';
        draft.organization.organizationLatestEndedEvents.error =
          event.payload.error;
        break;
      }
      case '[organization] events has recordings fetch started': {
        draft.organization.organizationEventsHasRecordings.status =
          'processing';
        draft.organization.organizationEventsHasRecordings.error = undefined;
        break;
      }
      case '[organization] events has recordings fetch success': {
        draft.organization.organizationEventsHasRecordings.status = 'success';
        event.payload.data.forEach(data => {
          draft.organization.organizationEventsHasRecordings.hasRecordingsMap[
            data.event_id
          ] = data.has_recordings;
        });
        break;
      }
      case '[organization] events has recordings fetch failed': {
        draft.organization.organizationEventsHasRecordings.status = 'failed';
        draft.organization.organizationEventsHasRecordings.error =
          event.payload.error;
        break;
      }
      case '[organization] latest ended events recordings fetch started': {
        draft.organization.organizationLatestEndedEventsRecordings.status =
          'processing';
        draft.organization.organizationLatestEndedEventsRecordings.error =
          undefined;
        break;
      }
      case '[organization] latest ended events recordings fetch success': {
        draft.organization.organizationLatestEndedEventsRecordings.status =
          'success';
        event.payload.data.forEach(data => {
          draft.organization.organizationLatestEndedEventsRecordings.recordingsMap[
            data.event_id
          ] = data.longest_recording_id;
        });
        break;
      }
      case '[organization] latest ended events recordings fetch failed': {
        draft.organization.organizationLatestEndedEventsRecordings.status =
          'failed';
        draft.organization.organizationLatestEndedEventsRecordings.error =
          event.payload.error;
        break;
      }
      case '[organization] event longest recording fetch success': {
        const { eventId, longestRecordingId } = event.payload;
        draft.organization.organizationLatestEndedEventsRecordings.recordingsMap[
          eventId
        ] = longestRecordingId || '';
        break;
      }
      case '[organization] event longest recording fetch failed': {
        draft.organization.organizationLatestEndedEventsRecordings.status =
          'failed';
        draft.organization.organizationLatestEndedEventsRecordings.error =
          event.payload.error;
        break;
      }
      case '[organization] show event duplicate not allowed modal': {
        draft.organization.showEventDuplicateNotAllowedModal =
          event.payload.showEventDuplicateNotAllowedModal;
        break;
      }
      case '[organization profile] profile update started': {
        draft.organization.profileUpdate.status = 'processing';
        break;
      }
      case '[organization profile] profile update success': {
        draft.organization.profileUpdate.status = 'success';

        draft.organization.organizationInfo.name = event.payload.name;
        draft.organization.organizationInfo.email = event.payload.email;
        draft.organization.organizationInfo.image =
          event.payload.image.original;
        draft.organization.organizationInfo.about = event.payload.about;
        draft.organization.organizationInfo.facebookUrl =
          event.payload.facebookUrl;
        draft.organization.organizationInfo.linkedinUrl =
          event.payload.linkedinUrl;
        draft.organization.organizationInfo.twitterUrl =
          event.payload.twitterUrl;
        draft.organization.organizationInfo.websiteUrl =
          event.payload.websiteUrl;

        // reconcile with NavBar
        draft.navBar.selectedOrgInfo.name = event.payload.name;
        draft.navBar.selectedOrgInfo.image = event.payload.image.small;
        draft.navBar.selectedOrgInfo.largeImage = event.payload.image.large;
        break;
      }
      case '[organization profile] profile update failed': {
        draft.organization.profileUpdate.status = 'failed';
        draft.organization.profileUpdate.fieldErrors =
          event.payload?.fieldErrors ?? null;
        break;
      }
      case '[team] update search query': {
        draft.organization.teamActiveMembers.searchQuery =
          event.payload.searchQuery;
        draft.organization.teamActiveMembers.pageInfo = {
          hasNextPage: false,
          endCursor: null,
        };
        draft.organization.teamActiveMembers.members = [];
        draft.organization.teamActiveMembers.status = 'processing';
        break;
      }
      case '[team] active members fetch started': {
        draft.organization.teamActiveMembers.status = 'processing';
        if (event.payload.isFirstPage) {
          draft.organization.teamActiveMembers.pageInfo = {
            hasNextPage: false,
            endCursor: null,
          };
        }
        break;
      }
      case '[team] active members fetch success': {
        draft.organization.teamActiveMembers.status = 'success';
        draft.organization.teamActiveMembers.ownerId = event.payload.ownerId;
        draft.organization.teamActiveMembers.members = event.payload.members;
        draft.organization.teamActiveMembers.pageInfo = event.payload.pageInfo;
        draft.organization.teamActiveMembers.hasFetchedWithSpaces =
          event.payload.hasFetchedWithSpaces;
        break;
      }
      case '[team] active members fetch failed': {
        draft.organization.teamActiveMembers.status = 'failed';
        draft.organization.teamActiveMembers.error = event.payload.error;
        break;
      }
      case '[spaces] update search query': {
        draft.organization.spaces.searchQuery = event.payload.searchQuery;
        draft.organization.spaces.spaces = [];
        draft.organization.spaces.pageInfo = {
          hasNextPage: false,
          endCursor: null,
        };
        draft.organization.spaces.status = 'processing';
        break;
      }
      case '[spaces] fetch spaces started': {
        draft.organization.spaces.status = 'processing';
        if (event.payload.isFirstPage) {
          draft.organization.spaces.pageInfo = {
            hasNextPage: false,
            endCursor: null,
          };
        }
        break;
      }
      case '[spaces] fetch spaces success': {
        draft.organization.spaces.status = 'success';
        draft.organization.spaces.spaces = event.payload.isFirstPage
          ? event.payload.spaces
          : [...draft.organization.spaces.spaces, ...event.payload.spaces];
        draft.organization.spaces.pageInfo = event.payload.pageInfo;
        break;
      }
      case '[spaces] fetch spaces failed': {
        draft.organization.spaces.status = 'failed';
        draft.organization.spaces.error = event.payload.error;
        break;
      }
      case '[spaces] create space modal open': {
        draft.organization.spaces.createSpaceForm.isModalOpen = true;
        break;
      }
      case '[spaces] create space modal close': {
        draft.organization.spaces.createSpaceForm = defaultCreateSpaceFormState;
        break;
      }
      case '[spaces] update create space name': {
        draft.organization.spaces.createSpaceForm.name = event.payload.name;
        draft.organization.spaces.createSpaceForm.errors = [];
        break;
      }
      case '[spaces] create space started': {
        draft.organization.spaces.createSpaceForm.isSubmitting = true;
        draft.organization.spaces.createSpaceForm.errors = [];
        break;
      }
      case '[spaces] create space success': {
        draft.organization.spaces.createSpaceForm = defaultCreateSpaceFormState;
        break;
      }
      case '[spaces] create space failed': {
        draft.organization.spaces.createSpaceForm.isSubmitting = false;
        draft.organization.spaces.createSpaceForm.errors = event.payload.errors;
        break;
      }
      case '[spaces] rename space modal open': {
        draft.organization.spaces.spaceToRename = event.payload.space;
        draft.organization.spaces.renameSpaceStatus = 'idle';
        draft.organization.spaces.renameSpaceError = null;
        break;
      }
      case '[spaces] rename space modal close': {
        draft.organization.spaces.spaceToRename = null;
        draft.organization.spaces.renameSpaceStatus = 'idle';
        draft.organization.spaces.renameSpaceError = null;
        break;
      }
      case '[spaces] rename space started': {
        draft.organization.spaces.renameSpaceStatus = 'processing';
        draft.organization.spaces.renameSpaceError = null;
        break;
      }
      case '[spaces] rename space success': {
        draft.organization.spaces.spaceToRename = null;
        draft.organization.spaces.renameSpaceStatus = 'success';
        draft.organization.spaces.renameSpaceError = null;

        // Keep search results consistent: remove the renamed space if it's new name
        // no longer matches the active query; otherwise update it in place.
        const searchQuery = draft.organization.spaces.searchQuery
          .trim()
          .toLowerCase();

        const shouldRemoveSpace =
          searchQuery.length > 0 &&
          !event.payload.name.toLowerCase().includes(searchQuery);

        if (shouldRemoveSpace) {
          draft.organization.spaces.spaces =
            draft.organization.spaces.spaces.filter(
              space => space.id !== event.payload.spaceId,
            );
        } else {
          const renamedSpace = draft.organization.spaces.spaces.find(
            space => space.id === event.payload.spaceId,
          );

          if (renamedSpace) {
            renamedSpace.name = event.payload.name;
          }
        }

        // Update the current space name if it was renamed
        if (draft.organization.currentSpace.id === event.payload.spaceId) {
          draft.organization.currentSpace.name = event.payload.name;
        }

        break;
      }
      case '[spaces] rename space failed': {
        draft.organization.spaces.renameSpaceStatus = 'failed';
        draft.organization.spaces.renameSpaceError = event.payload.error;
        break;
      }
      case '[spaces] rename space error clear': {
        draft.organization.spaces.renameSpaceError = null;
        break;
      }
      case '[team] update member role': {
        draft.organization.teamActiveMembers.members =
          draft.organization.teamActiveMembers.members.map(member => {
            if (member.id === event.payload.memberId) {
              return {
                ...member,
                role: event.payload.newRole,
              };
            }
            return member;
          });
        break;
      }
      case '[team] update member role failed': {
        draft.organization.teamActiveMembers.members =
          draft.organization.teamActiveMembers.members.map(member => {
            if (member.id === event.payload.memberId) {
              return {
                ...member,
                role: event.payload.oldRole,
              };
            }
            return member;
          });
        break;
      }
      case '[team] update member role success': {
        draft.organization.teamActiveMembers.members =
          draft.organization.teamActiveMembers.members.map(member => {
            if (member.id === event.payload.memberId) {
              return {
                ...member,
                role: event.payload.newRole,
              };
            }
            return member;
          });
        break;
      }
      case '[team] update member spaces': {
        draft.organization.teamActiveMembers.memberSpacesUpdateRequestIds[
          event.payload.memberId
        ] = event.payload.requestId;
        draft.organization.teamActiveMembers.members =
          draft.organization.teamActiveMembers.members.map(member => {
            if (member.id === event.payload.memberId) {
              return {
                ...member,
                spaces: event.payload.newSpaces,
              };
            }
            return member;
          });
        break;
      }
      case '[team] update member spaces success': {
        if (
          draft.organization.teamActiveMembers.memberSpacesUpdateRequestIds[
            event.payload.memberId
          ] !== event.payload.requestId
        ) {
          break;
        }

        delete draft.organization.teamActiveMembers
          .memberSpacesUpdateRequestIds[event.payload.memberId];
        draft.organization.teamActiveMembers.members =
          draft.organization.teamActiveMembers.members.map(member => {
            if (member.id === event.payload.memberId) {
              return {
                ...member,
                spaces: event.payload.newSpaces,
              };
            }
            return member;
          });
        break;
      }
      case '[team] update member spaces failed': {
        if (
          draft.organization.teamActiveMembers.memberSpacesUpdateRequestIds[
            event.payload.memberId
          ] !== event.payload.requestId
        ) {
          break;
        }

        delete draft.organization.teamActiveMembers
          .memberSpacesUpdateRequestIds[event.payload.memberId];
        draft.organization.teamActiveMembers.members =
          draft.organization.teamActiveMembers.members.map(member => {
            if (member.id === event.payload.memberId) {
              return {
                ...member,
                spaces: event.payload.oldSpaces,
              };
            }
            return member;
          });
        break;
      }
      case '[team] remove member success': {
        draft.organization.teamActiveMembers.members =
          draft.organization.teamActiveMembers.members.filter(
            member => member.id !== event.payload.memberId,
          );
        break;
      }
      case '[team] fetch team pending invites started': {
        draft.organization.teamPendingInvites.status = 'processing';
        if (event.payload.isFirstPage) {
          draft.organization.teamPendingInvites.pageInfo = {
            hasNextPage: false,
            endCursor: null,
          };
        }
        break;
      }
      case '[team] fetch team pending invites success': {
        draft.organization.teamPendingInvites.status = 'success';
        draft.organization.teamPendingInvites.pendingInvites =
          event.payload.pendingInvites;
        draft.organization.teamPendingInvites.pageInfo = event.payload.pageInfo;
        break;
      }
      case '[team] fetch team pending invites failed': {
        draft.organization.teamPendingInvites.status = 'failed';
        draft.organization.teamPendingInvites.error = event.payload.error;
        break;
      }
      case '[team] update pending invites search query': {
        draft.organization.teamPendingInvites.searchQuery =
          event.payload.searchQuery;
        draft.organization.teamPendingInvites.pageInfo = {
          hasNextPage: false,
          endCursor: null,
        };
        draft.organization.teamPendingInvites.pendingInvites = [];
        draft.organization.teamPendingInvites.status = 'processing';
        break;
      }
      case '[team] remove pending invite success': {
        draft.organization.teamPendingInvites.pendingInvites =
          draft.organization.teamPendingInvites.pendingInvites.filter(
            invite => invite.id !== event.payload.inviteId,
          );
        break;
      }
      case '[team] export audit report started': {
        draft.organization.manageTeam.auditExport.status = 'processing';
        break;
      }
      case '[team] export audit report success': {
        draft.organization.manageTeam.auditExport.status = 'idle';
        break;
      }
      case '[team] export audit report failed': {
        draft.organization.manageTeam.auditExport.status = 'failed';
        draft.organization.manageTeam.auditExport.error = event.payload.error;
        break;
      }
      case '[team] fetch organisation payment details started': {
        draft.organization.manageTeam.organisationPaymentDetails.status =
          'processing';
        break;
      }
      case '[team] fetch organisation payment details success': {
        draft.organization.manageTeam.organisationPaymentDetails.status =
          'success';
        draft.organization.manageTeam.organisationPaymentDetails.data =
          event.payload.data;
        break;
      }
      case '[team] fetch organisation payment details failed': {
        draft.organization.manageTeam.organisationPaymentDetails.status =
          'failed';
        draft.organization.manageTeam.organisationPaymentDetails.error =
          event.payload.error;
        break;
      }
      case '[team] invite member started': {
        draft.organization.manageTeam.inviteMemberForm.isSubmitting = true;
        draft.organization.manageTeam.inviteMemberForm.isSuccess = false;
        draft.organization.manageTeam.inviteMemberForm.errors = [];
        break;
      }
      case '[team] invite member success': {
        draft.organization.manageTeam.inviteMemberForm.isSubmitting = false;
        draft.organization.manageTeam.inviteMemberForm.isSuccess = true;
        draft.organization.manageTeam.inviteMemberForm.errors = [];
        break;
      }
      case '[team] invite member failed': {
        draft.organization.manageTeam.inviteMemberForm.isSubmitting = false;
        draft.organization.manageTeam.inviteMemberForm.isSuccess = false;
        draft.organization.manageTeam.inviteMemberForm.errors = [
          { field: 'root', message: event.payload.error.message },
        ];
        break;
      }
      case '[team] invite member validation error': {
        draft.organization.manageTeam.inviteMemberForm.isSubmitting = false;
        draft.organization.manageTeam.inviteMemberForm.isSuccess = false;
        draft.organization.manageTeam.inviteMemberForm.errors =
          event.payload.errors;
        break;
      }
      case '[team] invite member modal open': {
        draft.organization.manageTeam.inviteMemberForm.isModalOpen = true;
        break;
      }
      case '[team] invite member modal close': {
        draft.organization.manageTeam.inviteMemberForm =
          defaultInviteMemberFormState;
        break;
      }
      case '[team] fetch manageable spaces started': {
        draft.organization.manageTeam.manageableSpaces.status = 'processing';
        break;
      }
      case '[team] fetch manageable spaces success': {
        draft.organization.manageTeam.manageableSpaces.status = 'success';
        draft.organization.manageTeam.manageableSpaces.spaces = event.payload
          .append
          ? [
              ...draft.organization.manageTeam.manageableSpaces.spaces,
              ...event.payload.spaces,
            ]
          : event.payload.spaces;
        draft.organization.manageTeam.manageableSpaces.pageInfo =
          event.payload.pageInfo;
        break;
      }
      case '[team] fetch manageable spaces failed': {
        draft.organization.manageTeam.manageableSpaces.status = 'failed';
        break;
      }
      case '[organization] fetch org spaces started': {
        draft.organization.orgSpaces.status = 'processing';
        break;
      }
      case '[organization] fetch org spaces success': {
        draft.organization.orgSpaces.status = 'success';
        draft.organization.orgSpaces.spaces = event.payload.append
          ? [...draft.organization.orgSpaces.spaces, ...event.payload.spaces]
          : event.payload.spaces;
        draft.organization.orgSpaces.pageInfo = event.payload.pageInfo;
        break;
      }
      case '[organization] fetch org spaces failed': {
        draft.organization.orgSpaces.status = 'failed';
        break;
      }
      case '[organization] initial spaceid resolved': {
        draft.organization.currentSpace.id =
          event.payload.spaceExternalId || '';
        draft.organization.currentSpace.name = event.payload.spaceName || '';
        draft.organization.initialSpaceIdResolved = event.payload.resolved;
        break;
      }
      case '[organization] invalid space access modal open': {
        draft.organization.invalidSpaceAccessModal = {
          isOpen: true,
          previousSpaceName: event.payload.previousSpaceName,
          newSpaceName: event.payload.newSpaceName,
        };
        break;
      }
      case '[organization] invalid space access modal close': {
        draft.organization.invalidSpaceAccessModal = {
          isOpen: false,
          previousSpaceName: '',
          newSpaceName: '',
        };
        break;
      }
      case '[event-team-access] switch tab': {
        draft.organization.eventTeamAccess.activeTab = event.payload.activeTab;
        if (event.payload.activeTab === 'with-access') {
          draft.organization.eventTeamAccess.withAccess.pageInfo = {
            hasNextPage: false,
            endCursor: null,
          };
        }
        if (event.payload.activeTab === 'without-access') {
          draft.organization.eventTeamAccess.withoutAccess.pageInfo = {
            hasNextPage: false,
            endCursor: null,
          };
        }
        break;
      }
      case '[event-team-access] update event id': {
        let resetMembers = false;
        if (
          draft.organization.eventTeamAccess.eventId !== event.payload.eventId
        ) {
          resetMembers = true;
        }
        draft.organization.eventTeamAccess.eventId = event.payload.eventId;
        draft.organization.eventTeamAccess.activeTab = 'with-access';
        draft.organization.eventTeamAccess.withAccess.pageInfo = {
          hasNextPage: false,
          endCursor: null,
        };
        draft.organization.eventTeamAccess.withoutAccess.pageInfo = {
          hasNextPage: false,
          endCursor: null,
        };
        if (resetMembers) {
          draft.organization.eventTeamAccess.withAccess.members = [];
          draft.organization.eventTeamAccess.withoutAccess.members = [];
        }
        break;
      }
      case '[event-team-access] with-access members fetch started': {
        draft.organization.eventTeamAccess.withAccess.status = 'processing';
        break;
      }
      case '[event-team-access] with-access members fetch success': {
        draft.organization.eventTeamAccess.withAccess.status = 'success';
        draft.organization.eventTeamAccess.eventId = event.payload.eventId;
        draft.organization.eventTeamAccess.withAccess.members = event.payload
          .isFirstPage
          ? event.payload.members
          : event.payload.members.length > 20 // #TODO: we have existing issue in prod related to urql client cache, this just done to avoid the existing issue. Need to investigate the root cause and fix it.
          ? event.payload.members
          : [
              ...draft.organization.eventTeamAccess.withAccess.members,
              ...event.payload.members,
            ];
        draft.organization.eventTeamAccess.withAccess.pageInfo =
          event.payload.pageInfo;
        break;
      }
      case '[event-team-access] with-access members fetch failed': {
        draft.organization.eventTeamAccess.withAccess.status = 'failed';
        draft.organization.eventTeamAccess.withAccess.error =
          event.payload.error;
        draft.organization.eventTeamAccess.withAccess.pageInfo = {
          hasNextPage: false,
          endCursor: null,
        };
        draft.organization.eventTeamAccess.withAccess.members = [];
        break;
      }
      case '[event-team-access] without-access members fetch started': {
        draft.organization.eventTeamAccess.withoutAccess.status = 'processing';
        break;
      }
      case '[event-team-access] without-access members fetch success': {
        draft.organization.eventTeamAccess.withoutAccess.status = 'success';
        draft.organization.eventTeamAccess.eventId = event.payload.eventId;
        draft.organization.eventTeamAccess.withoutAccess.members = event.payload
          .isFirstPage
          ? event.payload.members
          : event.payload.members.length > 20 // #TODO: we have existing issue in prod, this just done to avoid the existing issue. Need to investigate the root cause and fix it.
          ? event.payload.members
          : [
              ...draft.organization.eventTeamAccess.withoutAccess.members,
              ...event.payload.members,
            ];
        draft.organization.eventTeamAccess.withoutAccess.pageInfo =
          event.payload.pageInfo;
        break;
      }
      case '[event-team-access] without-access members fetch failed': {
        draft.organization.eventTeamAccess.withoutAccess.status = 'failed';
        draft.organization.eventTeamAccess.withoutAccess.error =
          event.payload.error;
        draft.organization.eventTeamAccess.withoutAccess.pageInfo = {
          hasNextPage: false,
          endCursor: null,
        };
        draft.organization.eventTeamAccess.withoutAccess.members = [];
        break;
      }
      case '[event-team-access] update search query': {
        const activeTab = draft.organization.eventTeamAccess.activeTab;
        const tabState =
          activeTab === 'with-access'
            ? draft.organization.eventTeamAccess.withAccess
            : draft.organization.eventTeamAccess.withoutAccess;
        tabState.searchQuery = event.payload.searchQuery;
        tabState.pageInfo = {
          hasNextPage: false,
          endCursor: null,
        };
        break;
      }
      case '[event-team-access] clear search query': {
        const activeTab = draft.organization.eventTeamAccess.activeTab;
        const tabState =
          activeTab === 'with-access'
            ? draft.organization.eventTeamAccess.withAccess
            : draft.organization.eventTeamAccess.withoutAccess;
        tabState.searchQuery = '';
        tabState.pageInfo = {
          hasNextPage: false,
          endCursor: null,
        };
        break;
      }
      case '[event-team-access] update sort key': {
        const activeTab = draft.organization.eventTeamAccess.activeTab;
        const tabState =
          activeTab === 'with-access'
            ? draft.organization.eventTeamAccess.withAccess
            : draft.organization.eventTeamAccess.withoutAccess;
        tabState.sortKey = event.payload.sortKey;
        tabState.reverse = event.payload.reverse || false;
        tabState.pageInfo = {
          hasNextPage: false,
          endCursor: null,
        };
        break;
      }
      case '[event-team-access] load more members': {
        const activeTab = draft.organization.eventTeamAccess.activeTab;
        const tabState =
          activeTab === 'with-access'
            ? draft.organization.eventTeamAccess.withAccess
            : draft.organization.eventTeamAccess.withoutAccess;
        tabState.status = 'processing';
        break;
      }
      case '[event-team-access] member access change started': {
        const { memberId, tab } = event.payload;
        if (tab === 'with-access') {
          draft.organization.eventTeamAccess.withAccess.members =
            draft.organization.eventTeamAccess.withAccess.members.map(
              member => {
                if (member.id === memberId) {
                  return { ...member, isUpdating: true };
                }
                return member;
              },
            );
        } else {
          draft.organization.eventTeamAccess.withoutAccess.members =
            draft.organization.eventTeamAccess.withoutAccess.members.map(
              member => {
                if (member.id === memberId) {
                  return { ...member, isUpdating: true };
                }
                return member;
              },
            );
        }
        break;
      }
      case '[event-team-access] member access change success': {
        const { memberId, tab } = event.payload;
        if (tab === 'with-access') {
          draft.organization.eventTeamAccess.withAccess.members =
            draft.organization.eventTeamAccess.withAccess.members.filter(
              member => {
                return member.id !== memberId;
              },
            );
        } else {
          draft.organization.eventTeamAccess.withoutAccess.members =
            draft.organization.eventTeamAccess.withoutAccess.members.filter(
              member => {
                return member.id !== memberId;
              },
            );
        }
        break;
      }
      case '[event-team-access] member access change failed': {
        const { memberId, tab } = event.payload;
        if (tab === 'with-access') {
          draft.organization.eventTeamAccess.withAccess.members =
            draft.organization.eventTeamAccess.withAccess.members.map(
              member => {
                if (member.id === memberId) {
                  return { ...member, isUpdating: false };
                }
                return member;
              },
            );
        } else {
          draft.organization.eventTeamAccess.withoutAccess.members =
            draft.organization.eventTeamAccess.withoutAccess.members.map(
              member => {
                if (member.id === memberId) {
                  return { ...member, isUpdating: false };
                }
                return member;
              },
            );
        }
        break;
      }
      case '[event-team-access] bulk access change started': {
        draft.organization.eventTeamAccess.isBulkActionPending = true;
        break;
      }
      case '[event-team-access] bulk access change success': {
        draft.organization.eventTeamAccess.isBulkActionPending = false;
        const activeTab = draft.organization.eventTeamAccess.activeTab;
        if (activeTab === 'with-access') {
          draft.organization.eventTeamAccess.withAccess.pageInfo = {
            hasNextPage: false,
            endCursor: null,
          };
        } else {
          draft.organization.eventTeamAccess.withoutAccess.pageInfo = {
            hasNextPage: false,
            endCursor: null,
          };
        }
        break;
      }
      case '[event-team-access] bulk access change failed': {
        draft.organization.eventTeamAccess.isBulkActionPending = false;
        break;
      }
      case '[recording] fetch recording started': {
        draft.contentHub.recordingInfo.status = 'processing';
        draft.contentHub.recordingInfo.data = null;
        draft.contentHub.recordingInfo.error = undefined;

        // Reset creatorLab state when loading a new recording
        draft.contentHub.creatorLab.contentHistory =
          createDefaultContentHistory();
        draft.contentHub.creatorLab.selectedContent =
          createDefaultCreatorLabSelectedContent('SOCIAL_POST');
        draft.contentHub.creatorLab.generatedContent = null;
        draft.contentHub.creatorLab.isContentGenerationRequestInProgress =
          false;

        // Reset contentComposer state when loading a new recording
        draft.contentHub.contentComposer = createDefaultContentComposerState();

        // Reset advanced edits state when loading a new recording
        draft.contentHub.recordingInfo.advancedEdits =
          createDefaultAdvancedEditsState();
        break;
      }
      case '[recording] fetch recording success': {
        draft.contentHub.recordingInfo.data = event.payload;
        draft.contentHub.recordingInfo.status = 'success';
        draft.contentHub.recordingInfo.playback = {
          currentTime: 0,
          duration: 0,
          isPlaying: false,
          isPortraitFullscreen: false,
          muted: false,
        };
        break;
      }
      case '[recording] toggle publish success': {
        draft.contentHub.recordingInfo.data = {
          ...draft.contentHub.recordingInfo.data,
          ...event.payload,
        };
        break;
      }
      case '[recording] fetch recording failed': {
        draft.contentHub.recordingInfo.data = null;
        draft.contentHub.recordingInfo.status = 'failed';
        draft.contentHub.recordingInfo.error = {
          message: event.payload.error.message,
        };
        draft.contentHub.recordingInfo.advancedEdits =
          createDefaultAdvancedEditsState();
        break;
      }
      case '[recording] fetch recording reset': {
        draft.contentHub.recordingInfo.data = null;
        draft.contentHub.recordingInfo.status = 'idle';
        draft.contentHub.recordingInfo.advancedEdits =
          createDefaultAdvancedEditsState();
        // Clear Content Composer generated content cache on recording change
        draft.contentHub.contentComposer.generatedContent = {};
        break;
      }
      case '[recording] set back url': {
        draft.contentHub.recordingInfo.backUrl = event.payload.backUrl;
        break;
      }
      case '[recording] fetch event info success': {
        draft.contentHub.recordingInfo.eventInfo = event.payload;
        break;
      }
      case '[recording] fetch event info failed': {
        draft.contentHub.recordingInfo.eventInfo = null;
        break;
      }
      case '[recording] fetch speakers started': {
        draft.contentHub.recordingInfo.eventSpeakers.status = 'processing';
        break;
      }
      case '[recording] fetch speakers success': {
        draft.contentHub.recordingInfo.eventSpeakers.data = event.payload;
        draft.contentHub.recordingInfo.eventSpeakers.status = 'success';
        break;
      }
      case '[recording] fetch speakers failed': {
        draft.contentHub.recordingInfo.eventSpeakers = {
          data: [],
          pagination: {
            orgSpeakers: {
              next_cursor: null,
            },
            eventSpeakers: {
              endCursor: null,
              hasNextPage: false,
            },
          },
          searchText: '',
          status: 'failed',
          errors: {
            message: event.payload.error.message,
          },
        };
        break;
      }
      case '[recording] fetch speakers v2 started': {
        draft.contentHub.recordingInfo.eventSpeakers.status = 'processing';
        break;
      }
      case '[recording] fetch speakers v2 success': {
        const { orgSpeakers, eventSpeakers } = event.payload;
        const currentData =
          draft.contentHub.recordingInfo.eventSpeakers.data || [];
        const wasProcessing =
          draft.contentHub.recordingInfo.eventSpeakers.status === 'processing';
        const hadOrgCursor =
          draft.contentHub.recordingInfo.eventSpeakers.pagination.orgSpeakers
            .next_cursor;
        const hadEventCursor =
          draft.contentHub.recordingInfo.eventSpeakers.pagination.eventSpeakers
            .hasNextPage;

        // Merge new speakers from both sources using mergeSpeakers to avoid duplicates
        const newSpeakers = mergeSpeakers(
          [...orgSpeakers.data],
          eventSpeakers.data,
        );

        // Append if this was a pagination request, replace for initial load or search
        draft.contentHub.recordingInfo.eventSpeakers.data =
          wasProcessing && (hadOrgCursor || hadEventCursor)
            ? mergeSpeakers([...currentData], newSpeakers)
            : newSpeakers;

        // Update pagination cursors for both sources
        draft.contentHub.recordingInfo.eventSpeakers.pagination.orgSpeakers = {
          next_cursor: orgSpeakers.cursor,
        };
        draft.contentHub.recordingInfo.eventSpeakers.pagination.eventSpeakers =
          {
            endCursor: eventSpeakers.pageInfo.endCursor,
            hasNextPage: eventSpeakers.pageInfo.hasNextPage,
          };

        draft.contentHub.recordingInfo.eventSpeakers.status = 'success';
        break;
      }
      case '[recording] fetch speakers v2 failed': {
        draft.contentHub.recordingInfo.eventSpeakers.status = 'failed';
        if ('errors' in draft.contentHub.recordingInfo.eventSpeakers) {
          draft.contentHub.recordingInfo.eventSpeakers.errors = {
            message: event.payload.error.message,
          };
        }
        break;
      }
      case '[recording] fetch speakers next page': {
        draft.contentHub.recordingInfo.eventSpeakers.status = 'processing';
        break;
      }
      case '[recording] update speakers search': {
        draft.contentHub.recordingInfo.eventSpeakers.searchText =
          event.payload.searchText;
        break;
      }
      case '[recording] reset speakers list': {
        draft.contentHub.recordingInfo.eventSpeakers.data = [];
        draft.contentHub.recordingInfo.eventSpeakers.pagination.orgSpeakers = {
          next_cursor: null,
        };
        draft.contentHub.recordingInfo.eventSpeakers.pagination.eventSpeakers =
          {
            endCursor: null,
            hasNextPage: false,
          };
        draft.contentHub.recordingInfo.eventSpeakers.status = 'idle';
        break;
      }
      case '[recording] playback time updated': {
        draft.contentHub.recordingInfo.playback.currentTime =
          event.payload.currentTime;
        if (event.payload.duration) {
          draft.contentHub.recordingInfo.playback.duration =
            event.payload.duration;
        }
        break;
      }
      case '[recording] play state changed': {
        draft.contentHub.recordingInfo.playback.isPlaying =
          event.payload.isPlaying;
        break;
      }
      case '[recording] clear pending seek and play': {
        draft.contentHub.recordingInfo.playback.pendingSeekAndPlay = false;
        break;
      }
      case '[recording] portrait fullscreen changed': {
        draft.contentHub.recordingInfo.playback.isPortraitFullscreen =
          event.payload.isPortraitFullscreen;
        break;
      }
      case '[recording] mute state changed': {
        draft.contentHub.recordingInfo.playback.muted = event.payload.muted;
        break;
      }
      case '[recording] fetch editor state success': {
        const draftClipsWithColorIndex = event.payload.draft_clips
          .map(clip => ({
            ...clip,
            colorIndex: getColorIndex(clip.id),
          }))
          .sort((a, b) => {
            if (a.start_time_ms !== b.start_time_ms) {
              return a.start_time_ms - b.start_time_ms;
            }
            return a.end_time_ms - b.end_time_ms;
          });

        draft.contentHub.recordingInfo.editorState = {
          ...event.payload,
          draft_clips: draftClipsWithColorIndex,
          selectedClip: null,
        };
        break;
      }
      case '[recording] fetch editor state failed': {
        draft.contentHub.recordingInfo.editorState = {
          clip_suggestion: null,
          draft_clips: [],
          selectedClip: null,
        };
        break;
      }
      case '[recording] [empty list] create new clip':
      case '[recording] [list header] create new clip':
      case '[recording] [initial view] create new clip': {
        const recording = draft.contentHub.recordingInfo.data;
        const playbackState = draft.contentHub.recordingInfo.playback;

        if (recording && playbackState) {
          // Create a new clip with current playback time as start and recording duration as end
          const currentTimeMs = Math.floor(playbackState.currentTime * 1000);
          const endTimeMs = Math.floor(recording.duration * 1000);

          const clipIndex =
            draft.contentHub.recordingInfo.editorState.draft_clips.length + 1;
          const clipId = generateClipId();
          const newClip: DraftClip = {
            id: clipId,
            title: `Clip ${clipIndex}`,
            start_time_ms: currentTimeMs,
            end_time_ms: endTimeMs,
            source_type: 'manual',
            isTemp: true,
            colorIndex: getColorIndex(clipId),
          };

          draft.contentHub.recordingInfo.editorState.draft_clips.unshift(
            newClip,
          );
          draft.contentHub.recordingInfo.editorState.selectedClip = newClip;
        }
        break;
      }
      case '[recording] create full clip for portrait':
      case '[recording] create full clip for landscape': {
        const recording = draft.contentHub.recordingInfo.data;
        const aspectRatio =
          event.type === '[recording] create full clip for portrait'
            ? ('9_16' as const)
            : ('16_9' as const);
        if (recording) {
          const clipIndex =
            draft.contentHub.recordingInfo.editorState.draft_clips.length + 1;
          const endTimeMs = Math.floor(recording.duration * 1000);
          const clipId = generateClipId();
          const presets = draft.contentHub.recordingInfo.colorPresets.presets;
          const defaultPreset = getDefaultPreset(presets);

          const newClip: DraftClip = {
            id: clipId,
            title: `Clip ${clipIndex}`,
            start_time_ms: 0,
            end_time_ms: endTimeMs,
            source_type: 'manual',
            isTemp: true,
            aspect_ratio: aspectRatio,
            colorIndex: getColorIndex(clipId),
            preset_id: defaultPreset?.id,
            lastUsedBackgroundColor:
              getClipPresetBackgroundColor(defaultPreset?.id, presets) ??
              undefined,
          };

          draft.contentHub.recordingInfo.editorState.draft_clips.unshift(
            newClip,
          );
          draft.contentHub.recordingInfo.editorState.selectedClip = newClip;
        }
        break;
      }
      case '[recording] create clip started':
      case '[recording] update clip started': {
        draft.contentHub.recordingInfo.clipUpdateInfo = {
          status: 'processing',
        };
        break;
      }
      case '[recording] create clip success':
      case '[recording] update clip success': {
        const { clipData } = event.payload;
        const clipIndex =
          draft.contentHub.recordingInfo.editorState.draft_clips.findIndex(
            c => c.id === clipData.id,
          );
        if (clipIndex !== -1) {
          const existingClip =
            draft.contentHub.recordingInfo.editorState.draft_clips[clipIndex];
          draft.contentHub.recordingInfo.editorState.draft_clips[clipIndex] = {
            ...existingClip,
            ...clipData,
            isTemp: false,
          };
        }
        draft.contentHub.recordingInfo.editorState.selectedClip = null;
        draft.contentHub.recordingInfo.editorState.draft_clips.sort((a, b) => {
          if (a.start_time_ms !== b.start_time_ms) {
            return a.start_time_ms - b.start_time_ms;
          }
          return a.end_time_ms - b.end_time_ms;
        });
        draft.contentHub.recordingInfo.clipUpdateInfo = {
          status: 'success',
        };
        break;
      }
      case '[recording] create clip failed':
      case '[recording] update clip failed': {
        draft.contentHub.recordingInfo.clipUpdateInfo = {
          status: 'failed',
          errors: {
            message: event.payload.error.message,
          },
        };
        break;
      }
      case '[recording] show clip delete dialog': {
        draft.contentHub.recordingInfo.clipDeletionInfo = {
          isDialogOpen: true,
          clipId: event.payload.clipId,
          clipTitle: event.payload.clipTitle,
          clipType: event.payload.clipType,
          status: 'idle',
        };
        break;
      }
      case '[recording] clip delete cancelled': {
        draft.contentHub.recordingInfo.clipDeletionInfo = {
          isDialogOpen: false,
          status: 'idle',
        };
        break;
      }
      case '[recording] clip delete started': {
        draft.contentHub.recordingInfo.clipDeletionInfo = {
          ...draft.contentHub.recordingInfo.clipDeletionInfo,
          status: 'processing',
        };
        break;
      }
      case '[recording] clip delete success': {
        const clipId = draft.contentHub.recordingInfo.clipDeletionInfo.clipId;
        if (clipId) {
          draft.contentHub.recordingInfo.editorState.draft_clips =
            draft.contentHub.recordingInfo.editorState.draft_clips.filter(
              c => c.id !== clipId,
            );
        }
        draft.contentHub.recordingInfo.clipDeletionInfo = {
          isDialogOpen: false,
          status: 'success',
        };
        break;
      }
      case '[recording] clip delete failed': {
        draft.contentHub.recordingInfo.clipDeletionInfo = {
          ...draft.contentHub.recordingInfo.clipDeletionInfo,
          status: 'failed',
          errors: {},
        };
        break;
      }
      case '[recording] discard all clips clicked': {
        draft.contentHub.recordingInfo.clipDiscardAllInfo = {
          isDialogOpen: true,
          status: 'idle',
        };
        break;
      }
      case '[recording] discard all clips cancel clicked': {
        draft.contentHub.recordingInfo.clipDiscardAllInfo = {
          isDialogOpen: false,
          status: 'idle',
        };
        break;
      }
      case '[recording] discard all clips started': {
        draft.contentHub.recordingInfo.clipDiscardAllInfo = {
          ...draft.contentHub.recordingInfo.clipDiscardAllInfo,
          status: 'processing',
        };
        break;
      }
      case '[recording] discard all clips success': {
        draft.contentHub.recordingInfo.editorState.draft_clips = [];
        draft.contentHub.recordingInfo.editorState.selectedClip = null;
        draft.contentHub.recordingInfo.clipDiscardAllInfo = {
          isDialogOpen: false,
          status: 'success',
        };
        break;
      }
      case '[recording] discard all clips failed': {
        draft.contentHub.recordingInfo.clipDiscardAllInfo = {
          ...draft.contentHub.recordingInfo.clipDiscardAllInfo,
          status: 'failed',
          errors: {},
        };
        break;
      }
      case '[recording] export clips clicked': {
        draft.contentHub.recordingInfo.clipExportInfo = {
          isDialogOpen: true,
          clipsToExport: event.payload.clipsToExport,
          status: 'idle',
        };
        break;
      }
      case '[recording] cancel export clips clicked': {
        draft.contentHub.recordingInfo.clipExportInfo = {
          isDialogOpen: false,
          clipsToExport: [],
          status: 'idle',
        };
        break;
      }
      case '[recording] export clips confirm clicked': {
        draft.contentHub.recordingInfo.clipExportInfo = {
          ...draft.contentHub.recordingInfo.clipExportInfo,
          status: 'processing',
        };
        break;
      }
      case '[recording] clip export success': {
        // Remove exported clips from draft_clips
        draft.contentHub.recordingInfo.editorState.draft_clips =
          draft.contentHub.recordingInfo.editorState.draft_clips.filter(
            clip => !event.payload.exportedClipIds.includes(clip.id),
          );

        draft.contentHub.recordingInfo.clipExportInfo = {
          isDialogOpen: false,
          clipsToExport: [],
          status: 'success',
        };
        break;
      }
      case '[recording] clip export failed': {
        draft.contentHub.recordingInfo.clipExportInfo = {
          ...draft.contentHub.recordingInfo.clipExportInfo,
          status: 'failed',
          errors: {},
        };
        break;
      }
      case '[recording] update description started': {
        draft.contentHub.recordingInfo.descriptionUpdateState = {
          status: 'processing',
        };
        break;
      }
      case '[recording] update description success': {
        draft.contentHub.recordingInfo.descriptionUpdateState = {
          status: 'success',
        };
        draft.contentHub.recordingInfo.data = {
          ...draft.contentHub.recordingInfo.data,
          ...event.payload,
        };
        break;
      }
      case '[recording] update description failed': {
        draft.contentHub.recordingInfo.descriptionUpdateState = {
          status: 'failed',
          errors: {
            message: event.payload.error.message,
          },
        };
        break;
      }
      case '[recording] [clip list] select clip for editing and play': {
        const { clipId } = event.payload;
        const currentSelected =
          draft.contentHub.recordingInfo.editorState.selectedClip;
        const draftClips =
          draft.contentHub.recordingInfo.editorState.draft_clips;

        if (currentSelected?.id === clipId) {
          // Same clip already selected – keep it to preserve unsaved edits (times, caption, etc.), just seek and play
          draft.contentHub.recordingInfo.playback.pendingSeekAndPlay = true;
        } else {
          const newClip = draftClips.find(clip => clip.id === clipId) ?? null;
          draft.contentHub.recordingInfo.editorState.selectedClip = newClip;
          draft.contentHub.recordingInfo.playback.pendingSeekAndPlay = true;
          initializeSelectedClip(newClip);
        }
        break;
      }
      case '[recording] [clip list] select clip for editing':
      case '[recording] [track] select clip for editing': {
        const { clipId } = event.payload;
        const newClip =
          draft.contentHub.recordingInfo.editorState.draft_clips.find(
            clip => clip.id === clipId,
          ) ?? null;
        draft.contentHub.recordingInfo.editorState.selectedClip = newClip;
        initializeSelectedClip(newClip);
        break;
      }
      case '[recording] [clip list] deselect clip':
      case '[recording] [track] deselect clip':
      case '[recording] deselect clip': {
        const selectedClip =
          draft.contentHub.recordingInfo.editorState.selectedClip;
        if (selectedClip?.isTemp) {
          draft.contentHub.recordingInfo.editorState.draft_clips =
            draft.contentHub.recordingInfo.editorState.draft_clips.filter(
              c => c.id !== selectedClip.id,
            );
        }
        draft.contentHub.recordingInfo.editorState.selectedClip = null;
        draft.contentHub.recordingInfo.playback.pendingSeekAndPlay = false;
        break;
      }
      case '[recording] [clip list] clip title updated': {
        if (draft.contentHub.recordingInfo.editorState.selectedClip) {
          draft.contentHub.recordingInfo.editorState.selectedClip = {
            ...draft.contentHub.recordingInfo.editorState.selectedClip,
            ...event.payload,
          };
        }
        break;
      }
      case '[recording] [clip list] clip time updated':
      case '[recording] [track] clip time updated': {
        if (draft.contentHub.recordingInfo.editorState.selectedClip) {
          draft.contentHub.recordingInfo.editorState.selectedClip = {
            ...draft.contentHub.recordingInfo.editorState.selectedClip,
            ...event.payload,
          };
        }
        break;
      }
      case '[recording] transcription status updated': {
        const { recordingId, status } = event.payload;
        if (draft.contentHub.recordingInfo.data?.id === recordingId) {
          draft.contentHub.recordingInfo.data.transcription_status = status;
        }
        break;
      }
      case '[recording] suggest clip clicked':
      case '[recording] [initial view] suggest clip clicked': {
        draft.contentHub.recordingInfo.editorState.clip_suggestion = {
          state: 'in_progress',
          created_at: new Date().toISOString(),
        };
        break;
      }

      case '[recording] fetch suggested clips success': {
        const { draft_clips } = event.payload;
        const clipsWithColorIndex = draft_clips.map(clip => ({
          ...clip,
          colorIndex: getColorIndex(clip.id),
        }));

        draft.contentHub.recordingInfo.editorState.draft_clips.push(
          ...clipsWithColorIndex,
        );

        draft.contentHub.recordingInfo.editorState.draft_clips.sort((a, b) => {
          if (a.start_time_ms !== b.start_time_ms) {
            return a.start_time_ms - b.start_time_ms;
          }
          return a.end_time_ms - b.end_time_ms;
        });

        draft.contentHub.recordingInfo.editorState.clip_suggestion = {
          state: 'finished',
          created_at: new Date().toISOString(),
        };
        break;
      }
      case '[recording] suggest clip success': {
        draft.contentHub.recordingInfo.editorState.clip_suggestion =
          event.payload;
        break;
      }
      case '[recording] suggest clip failed': {
        draft.contentHub.recordingInfo.editorState.clip_suggestion = {
          state: 'failed',
          created_at: new Date().toISOString(),
        };
        break;
      }
      case '[recording] fetch suggested clips failed': {
        draft.contentHub.recordingInfo.editorState.clip_suggestion = {
          state: 'failed',
          created_at: new Date().toISOString(),
        };
        break;
      }
      case '[recording] suggestion process failed': {
        draft.contentHub.recordingInfo.editorState.clip_suggestion = {
          state: 'failed',
          created_at: new Date().toISOString(),
        };
        break;
      }

      case '[recording] update title started': {
        draft.contentHub.recordingInfo.titleEditState.status = 'processing';
        break;
      }
      case '[recording] update title success': {
        draft.contentHub.recordingInfo.titleEditState.status = 'success';
        if (draft.contentHub.recordingInfo.data) {
          draft.contentHub.recordingInfo.data.title = event.payload.title;
        }
        break;
      }
      case '[recording] update title failed': {
        draft.contentHub.recordingInfo.titleEditState.status = 'failed';
        break;
      }
      case '[recording] fetch transcript started': {
        draft.contentHub.recordingInfo.transcript = {
          data: null,
          status: 'processing',
        };
        break;
      }
      case '[recording] fetch transcript success': {
        draft.contentHub.recordingInfo.transcript = {
          data: event.payload,
          status: 'success',
        };
        break;
      }
      case '[recording] fetch transcript failed': {
        draft.contentHub.recordingInfo.transcript = {
          data: null,
          status: 'failed',
          errors: {
            message: event.payload.error.message,
          },
        };
        break;
      }
      case '[organization-create] submit create organization form': {
        draft.createOrganization.isSubmitting = true;
        draft.createOrganization.error = null;
        break;
      }
      case '[organization-create] create organization form submit failed': {
        draft.createOrganization.isSubmitting = false;
        draft.createOrganization.error = event.payload.error;
        break;
      }
      case '[recording] select speaker clicked': {
        draft.contentHub.recordingInfo.speakerManagement = {
          editingState: 'select-speaker',
          speakerKey: event.payload.speakerKey,
          currentSpeakerName: event.payload.currentSpeakerName,
          status: 'idle',
        };
        break;
      }
      case '[recording] speaker selection cancelled': {
        draft.contentHub.recordingInfo.speakerManagement = {
          editingState: 'closed',
          speakerKey: undefined,
          currentSpeakerName: undefined,
          status: 'idle',
        };
        break;
      }
      case '[recording] update speaker mapping success': {
        if (draft.contentHub.recordingInfo.transcript.data?.speakers) {
          draft.contentHub.recordingInfo.transcript.data.speakers = {
            ...draft.contentHub.recordingInfo.transcript.data.speakers,
            ...event.payload.speakers,
          };
        }
        draft.contentHub.recordingInfo.speakerManagement = {
          ...draft.contentHub.recordingInfo.speakerManagement,
          speakerKey: undefined,
          currentSpeakerName: undefined,
          status: 'idle',
        };
        break;
      }
      case '[recording] update speaker mapping failed': {
        draft.contentHub.recordingInfo.speakerManagement = {
          ...draft.contentHub.recordingInfo.speakerManagement,
          speakerKey: undefined,
          currentSpeakerName: undefined,
          status: 'idle',
        };
        break;
      }
      case '[recording] update speaker mapping started': {
        draft.contentHub.recordingInfo.speakerManagement = {
          ...draft.contentHub.recordingInfo.speakerManagement,
          editingState: 'closed',
          isUploading: undefined,
          uploadedImageUrl: undefined,
          status: 'processing',
        };
        break;
      }
      case '[attendee-profile] fetch attendee profile data started': {
        draft.attendeeProfile.status = 'processing';
        draft.attendeeProfile.formState.fieldErrors = null;
        break;
      }
      case '[attendee-profile] fetch attendee profile data success': {
        draft.attendeeProfile.profileData = event.payload.data;
        draft.attendeeProfile.status = 'success';
        break;
      }
      case '[attendee-profile] fetch attendee profile data failed': {
        draft.attendeeProfile.error = event.payload.error;
        draft.attendeeProfile.status = 'failed';
        break;
      }
      case '[attendee-profile] submit attendee profile form started': {
        draft.attendeeProfile.formState.isSubmitting = true;
        draft.attendeeProfile.formState.fieldErrors = null;
        break;
      }
      case '[attendee-profile] submit attendee profile form success': {
        draft.attendeeProfile.profileData = {
          id: draft.attendeeProfile.profileData?.id ?? '',
          firstName:
            event.payload.firstName ??
            draft.attendeeProfile.profileData?.firstName ??
            '',
          lastName:
            event.payload.lastName ??
            draft.attendeeProfile.profileData?.lastName ??
            '',
          email: draft.attendeeProfile.profileData?.email ?? '',
          headline:
            event.payload.headline ??
            draft.attendeeProfile.profileData?.headline ??
            '',
          linkedinUrl:
            event.payload.linkedinUrl ??
            draft.attendeeProfile.profileData?.linkedinUrl ??
            '',
          twitterUrl:
            event.payload.twitterUrl ??
            draft.attendeeProfile.profileData?.twitterUrl ??
            '',
          websiteUrl:
            event.payload.websiteUrl ??
            draft.attendeeProfile.profileData?.websiteUrl ??
            '',
          bio:
            event.payload.bio ?? draft.attendeeProfile.profileData?.bio ?? '',
          deleteConfirmationPhrase:
            draft.attendeeProfile.profileData?.deleteConfirmationPhrase ?? '',
          userLoggedInViaSso:
            event.payload.userLoggedInViaSso ??
            draft.attendeeProfile.profileData?.userLoggedInViaSso ??
            false,
          image: {
            url:
              event.payload.image?.url ??
              draft.attendeeProfile.profileData?.image?.url ??
              '',
          },
        };
        draft.attendeeProfile.formState.isSubmitting = false;
        draft.navBar.userProfile.data = {
          id: draft.navBar.userProfile.data?.id ?? '',
          email: draft.navBar.userProfile.data?.email ?? '',
          avatarImage:
            event.payload.image?.small ??
            draft.navBar.userProfile.data?.avatarImage ??
            '',
          largeAvatarImage:
            event.payload.image?.large ??
            draft.navBar.userProfile.data?.largeAvatarImage ??
            '',
          firstName:
            event.payload.firstName ||
            draft.navBar.userProfile.data?.firstName ||
            '',
          lastName:
            event.payload.lastName ||
            draft.navBar.userProfile.data?.lastName ||
            '',
          hasAcceptedTerms:
            draft.navBar.userProfile.data?.hasAcceptedTerms ?? true,
        };
        break;
      }
      case '[attendee-profile] submit attendee profile form failed': {
        draft.attendeeProfile.error = event.payload.error;
        draft.attendeeProfile.formState.isSubmitting = false;
        draft.attendeeProfile.formState.fieldErrors =
          event.payload.fieldErrors ?? null;
        break;
      }
      case '[attendee-profile] change email started': {
        draft.attendeeProfile.emailFormState.isSubmitting = true;
        draft.attendeeProfile.emailFormState.isSuccess = false;
        draft.attendeeProfile.emailFormState.fieldErrors = null;
        break;
      }
      case '[attendee-profile] change email success': {
        draft.attendeeProfile.emailFormState.isSubmitting = false;
        draft.attendeeProfile.emailFormState.isSuccess = true;
        break;
      }
      case '[attendee-profile] change email failed': {
        draft.attendeeProfile.emailFormState.isSubmitting = false;
        draft.attendeeProfile.emailFormState.isSuccess = false;
        draft.attendeeProfile.emailFormState.fieldErrors =
          event.payload.fieldErrors ?? null;
        break;
      }
      case '[attendee-profile] change password started': {
        draft.attendeeProfile.passwordFormState.isSubmitting = true;
        draft.attendeeProfile.passwordFormState.isSuccess = false;
        draft.attendeeProfile.passwordFormState.fieldErrors = null;
        break;
      }
      case '[attendee-profile] change password success': {
        draft.attendeeProfile.passwordFormState.isSubmitting = false;
        draft.attendeeProfile.passwordFormState.isSuccess = true;
        break;
      }
      case '[attendee-profile] change password failed': {
        draft.attendeeProfile.passwordFormState.isSubmitting = false;
        draft.attendeeProfile.passwordFormState.isSuccess = false;
        draft.attendeeProfile.passwordFormState.fieldErrors =
          event.payload.fieldErrors ?? null;
        break;
      }
      case '[attendee-profile] fetch owned organizations started': {
        draft.attendeeProfile.deleteAccountFormState.isOwnedOrganizationsLoading =
          true;
        break;
      }
      case '[attendee-profile] fetch owned organizations success': {
        draft.attendeeProfile.deleteAccountFormState.ownedOrganizations =
          event.payload.data;
        draft.attendeeProfile.deleteAccountFormState.isOwnedOrganizationsLoading =
          false;
        break;
      }
      case '[attendee-profile] fetch owned organizations failed': {
        draft.attendeeProfile.deleteAccountFormState.isOwnedOrganizationsLoading =
          false;
        draft.attendeeProfile.deleteAccountFormState.error =
          event.payload.error;
        break;
      }
      case '[attendee] upcoming events fetch started': {
        draft.attendeeHome.upcoming.status = 'processing';
        draft.attendeeHome.upcoming.error = null;
        break;
      }
      case '[attendee] upcoming events fetch success': {
        draft.attendeeHome.upcoming.status = 'success';
        draft.attendeeHome.upcoming.items = [
          ...draft.attendeeHome.upcoming.items,
          ...event.payload.data,
        ];
        draft.attendeeHome.upcoming.pageInfo = event.payload.pageInfo;
        draft.attendeeHome.upcoming.error = null;
        break;
      }
      case '[attendee] upcoming events fetch failed': {
        draft.attendeeHome.upcoming.status = 'failed';
        draft.attendeeHome.upcoming.error = event.payload.error;
        break;
      }
      case '[attendee] past events fetch started': {
        draft.attendeeHome.past.status = 'processing';
        draft.attendeeHome.past.error = null;
        break;
      }
      case '[attendee] past events fetch success': {
        draft.attendeeHome.past.status = 'success';
        draft.attendeeHome.past.items = [
          ...draft.attendeeHome.past.items,
          ...event.payload.data,
        ];
        draft.attendeeHome.past.pageInfo = event.payload.pageInfo;
        draft.attendeeHome.past.error = null;
        break;
      }
      case '[attendee] past events fetch failed': {
        draft.attendeeHome.past.status = 'failed';
        draft.attendeeHome.past.error = event.payload.error;
        break;
      }
      case '[connections] fetch connections started': {
        draft.connections.status = 'processing';
        draft.connections.error = null;
        draft.connections.activeEventId = event.payload.eventId ?? null;
        if (event.payload.reset) {
          draft.connections.items = [];
          draft.connections.pageInfo = {
            hasNextPage: false,
            endCursor: null,
          };
        }
        break;
      }
      case '[connections] fetch connections success': {
        draft.connections.status = 'success';
        draft.connections.items = event.payload.reset
          ? event.payload.data
          : [...draft.connections.items, ...event.payload.data];
        draft.connections.pageInfo = event.payload.pageInfo;
        draft.connections.error = null;
        draft.connections.activeEventId = event.payload.eventId ?? null;
        break;
      }
      case '[connections] fetch connections failed': {
        draft.connections.status = 'failed';
        draft.connections.error = event.payload.error;
        draft.connections.activeEventId = event.payload.eventId ?? null;
        break;
      }
      case '[connections] delete connection success': {
        draft.connections.items = draft.connections.items.filter(
          connection => connection.id !== event.payload.registrantId,
        );
        draft.connections.error = null;
        break;
      }
      case '[connections] delete connection failed': {
        draft.connections.error = event.payload.error;
        break;
      }
      case '[connections] registered events filter fetch started': {
        draft.connections.eventFilter.status = 'processing';
        draft.connections.eventFilter.error = null;
        break;
      }
      case '[connections] registered events filter fetch success': {
        draft.connections.eventFilter.options = [
          ...draft.connections.eventFilter.options,
          ...event.payload.data,
        ];
        draft.connections.eventFilter.endCursor =
          event.payload.pageInfo.endCursor || null;
        draft.connections.eventFilter.hasMoreOptions =
          event.payload.pageInfo.hasNextPage;
        draft.connections.eventFilter.hasInitialised = true;
        draft.connections.eventFilter.status = 'success';
        draft.connections.eventFilter.error = null;
        break;
      }
      case '[connections] registered events filter fetch failed': {
        draft.connections.eventFilter.status = 'failed';
        draft.connections.eventFilter.hasMoreOptions = false;
        draft.connections.eventFilter.endCursor = null;
        draft.connections.eventFilter.error = event.payload.error;
        break;
      }
      case '[connections] registered events filter query reset': {
        draft.connections.eventFilter.options = [];
        draft.connections.eventFilter.endCursor = null;
        draft.connections.eventFilter.hasMoreOptions = false;
        break;
      }
      case '[connections] registered events filter query updated': {
        draft.connections.eventFilter.searchText = event.payload.query;
        break;
      }
      case '[connections] registered event selected': {
        draft.connections.eventFilter.selectedEvent =
          event.payload.event ?? undefined;
        break;
      }
      case '[content] update creator lab selected tab': {
        draft.contentHub.creatorLab.selectedContent =
          createDefaultCreatorLabSelectedContent(event.payload.selectedTab);

        const selectedContent = draft.contentHub.creatorLab.selectedContent;

        // Auto-select first email audience and first email type when switching to EMAIL tab
        if (event.payload.selectedTab === 'EMAIL') {
          if (isEmailContent(selectedContent)) {
            const emailAudienceConfig =
              draft.contentHub.creatorLab.emailAudienceConfig;
            if (
              emailAudienceConfig.status === 'success' &&
              emailAudienceConfig.data &&
              emailAudienceConfig.data.length > 0
            ) {
              const firstAudience = emailAudienceConfig.data[0];
              const firstType = firstAudience.types?.[0];
              if (firstAudience && firstType) {
                selectedContent.attributes.emailAudienceId = firstAudience.key;
                selectedContent.attributes.emailTypeId = firstType.key;
              }
            }
          }
        }

        // Auto-select first topic (overall summary) for EMAIL content type behind the scenes
        if (event.payload.selectedTab === 'EMAIL') {
          const contentRepurposing =
            draft.contentHub.creatorLab.contentRepurposing;
          if (
            contentRepurposing.status === 'success' &&
            contentRepurposing.data &&
            contentRepurposing.data.eligibility === 'ELIGIBLE' &&
            contentRepurposing.data.topics.status === 'FINISHED' &&
            contentRepurposing.data.topics.value &&
            contentRepurposing.data.topics.value.length > 0
          ) {
            const firstTopic = contentRepurposing.data.topics.value[0];
            if (firstTopic) {
              selectedContent.attributes.topicId = firstTopic.id;
            }
          }
        }

        break;
      }

      case '[content] fetch recent content started': {
        draft.contentHub.libraryInfo.recentContent.status = 'processing';
        break;
      }
      case '[content] fetch recent content success': {
        draft.contentHub.libraryInfo.recentContent.data = event.payload;
        draft.contentHub.libraryInfo.recentContent.status = 'success';
        break;
      }
      case '[content] fetch recent content failed': {
        draft.contentHub.libraryInfo.recentContent.status = 'failed';
        break;
      }
      case '[content] invalidate recent content': {
        draft.contentHub.libraryInfo.recentContent.status = 'idle';
        break;
      }
      case '[recording] create speaker clicked': {
        draft.contentHub.recordingInfo.speakerManagement = {
          editingState: 'create-speaker',
          speakerKey: event.payload.speakerKey,
          currentSpeakerName: event.payload.currentSpeakerName,
          status: 'idle',
        };
        break;
      }
      case '[recording] speaker creation cancelled':
      case '[recording] speaker edit cancelled': {
        draft.contentHub.recordingInfo.speakerManagement = {
          editingState: 'closed',
          speakerKey: undefined,
          speaker: undefined,
          currentSpeakerName: undefined,
          isUploading: undefined,
          uploadedImageUrl: undefined,
          status: 'idle',
        };
        break;
      }
      case '[recording] back to speaker selection': {
        draft.contentHub.recordingInfo.speakerManagement.editingState =
          'select-speaker';
        draft.contentHub.recordingInfo.speakerManagement.status = 'idle';
        break;
      }
      case '[recording] create speaker success': {
        if (draft.contentHub.recordingInfo.transcript.data?.speakers) {
          draft.contentHub.recordingInfo.transcript.data.speakers = {
            ...draft.contentHub.recordingInfo.transcript.data.speakers,
            ...event.payload.speakers,
          };
        }
        // Add the speaker to eventSpeakers list
        const newlyCreatedSpeaker =
          event.payload.speakers[event.payload.speakerKey];
        if (
          newlyCreatedSpeaker &&
          draft.contentHub.recordingInfo.eventSpeakers.data
        ) {
          draft.contentHub.recordingInfo.eventSpeakers.data.push(
            newlyCreatedSpeaker,
          );
        } else if (newlyCreatedSpeaker) {
          draft.contentHub.recordingInfo.eventSpeakers.data = [
            newlyCreatedSpeaker,
          ];
        }
        draft.contentHub.recordingInfo.speakerManagement = {
          editingState: 'closed',
          speakerKey: undefined,
          currentSpeakerName: undefined,
          isUploading: undefined,
          uploadedImageUrl: undefined,
          status: 'idle',
        };
        break;
      }
      case '[recording] create speaker failed': {
        console.error('Speaker creation failed:', event.payload.error);
        draft.contentHub.recordingInfo.speakerManagement.status = 'failed';
        break;
      }
      case '[recording] create speaker started': {
        draft.contentHub.recordingInfo.speakerManagement.status = 'processing';
        break;
      }
      case '[recording] speaker image upload started': {
        draft.contentHub.recordingInfo.speakerManagement.isUploading = true;
        break;
      }
      case '[recording] speaker image upload success': {
        draft.contentHub.recordingInfo.speakerManagement.isUploading = false;
        draft.contentHub.recordingInfo.speakerManagement.uploadedImageUrl =
          event.payload.imageUrl;
        break;
      }
      case '[recording] speaker image upload failed': {
        draft.contentHub.recordingInfo.speakerManagement.isUploading = false;
        console.error('Image upload failed:', event.payload.error);
        break;
      }
      case '[recording] update transcript search query': {
        draft.contentHub.recordingInfo.transcriptSearchQuery =
          event.payload.searchQuery;
        break;
      }
      case '[recording] clear transcript search query': {
        draft.contentHub.recordingInfo.transcriptSearchQuery = '';
        break;
      }
      case '[recording] context menu tab changed': {
        const tabId = event.payload.tabId;
        draft.contentHub.recordingInfo.selectedContextMenuTabId = tabId;
        break;
      }
      case '[recording] fetch color presets started': {
        draft.contentHub.recordingInfo.colorPresets.status = 'processing';
        break;
      }
      case '[recording] fetch color presets successful': {
        draft.contentHub.recordingInfo.colorPresets.status = 'success';
        draft.contentHub.recordingInfo.colorPresets.presets =
          event.payload.presets;
        // Set default preset ID on selected clip if not already set
        const selectedClip =
          draft.contentHub.recordingInfo.editorState.selectedClip;
        if (selectedClip) {
          setDefaultPresetOnClip(selectedClip, event.payload.presets);
        }
        break;
      }
      case '[recording] fetch color presets failed': {
        draft.contentHub.recordingInfo.colorPresets.status = 'failed';
        break;
      }
      case '[recording] colour preset changed': {
        const selectedClip =
          draft.contentHub.recordingInfo.editorState.selectedClip;
        const draftClips =
          draft.contentHub.recordingInfo.editorState.draft_clips;
        const preset = event.payload.preset;

        // Color mapping:
        // 1st (BRAND-PRIMARY / colorPrimary400) → active word color
        // 2nd (NEUTRAL-PRIMARY / colorWhite) → video player background
        // Caption text color is always white with black outline (not from preset)
        const activeWordColor = getPresetColor(preset, ['colorPrimary400']);
        const backgroundColor = getPresetColor(preset, [
          'colorWhite',
          'colorPrimary400',
        ]);

        // When preset changes, sync colors but preserve blur mode if user had blur selected.
        // Note: caption text color is read directly from preset in UI, not stored on clip
        if (selectedClip) {
          if (backgroundColor) {
            // Always update lastUsedBackgroundColor so color picker shows new preset and blur→color uses it
            syncLastUsedBackgroundColor(
              selectedClip,
              draftClips,
              backgroundColor,
            );
            // Only set background_color (solid color) if clip was not in blur mode; preserve blur
            const wasBlurMode = selectedClip.background_color === null;
            if (!wasBlurMode) {
              syncClipProperty(
                selectedClip,
                'background_color',
                backgroundColor,
              );
            }
          }
          if (activeWordColor) {
            syncClipProperty(selectedClip, 'highlight_color', activeWordColor);
            syncClipProperty(selectedClip, 'isCustomActiveWordColor', false);
          }
        }

        // Store preset ID on selected clip (API property for persistence)
        syncClipProperty(selectedClip, 'preset_id', event.payload.preset?.id);
        break;
      }
      case '[recording] video background color changed': {
        const selectedClip =
          draft.contentHub.recordingInfo.editorState.selectedClip;
        const draftClips =
          draft.contentHub.recordingInfo.editorState.draft_clips;

        // Update API property (hex color = no blur)
        syncClipProperty(selectedClip, 'background_color', event.payload.color);
        // Store as lastUsedBackgroundColor for blur toggle preservation
        syncLastUsedBackgroundColor(
          selectedClip,
          draftClips,
          event.payload.color,
        );
        break;
      }
      case '[recording] video background blur changed': {
        const selectedClip =
          draft.contentHub.recordingInfo.editorState.selectedClip;
        const draftClips =
          draft.contentHub.recordingInfo.editorState.draft_clips;
        const presets = draft.contentHub.recordingInfo.colorPresets.presets;

        // Update API property: null = blur mode, hex color = solid color
        if (event.payload.blur) {
          // Blur enabled → save current color to lastUsedBackgroundColor before setting to null
          if (
            selectedClip &&
            typeof selectedClip.background_color === 'string'
          ) {
            syncLastUsedBackgroundColor(
              selectedClip,
              draftClips,
              selectedClip.background_color,
            );
          }
          syncClipProperty(selectedClip, 'background_color', null);
        } else {
          // Blur disabled → restore lastUsedBackgroundColor or fallback to preset
          const restoreColor =
            selectedClip?.lastUsedBackgroundColor ||
            getClipPresetBackgroundColor(selectedClip?.preset_id, presets) ||
            DEFAULT_VIDEO_BG_COLOR;
          syncClipProperty(selectedClip, 'background_color', restoreColor);
        }
        break;
      }
      case '[recording] video background reset to preset': {
        const selectedClip =
          draft.contentHub.recordingInfo.editorState.selectedClip;
        const draftClips =
          draft.contentHub.recordingInfo.editorState.draft_clips;
        const presets = draft.contentHub.recordingInfo.colorPresets.presets;

        // Get current preset's background color
        const presetColor = getClipPresetBackgroundColor(
          selectedClip?.preset_id,
          presets,
        );

        // Reset to preset: update API property to preset color (no blur)
        if (presetColor) {
          syncClipProperty(selectedClip, 'background_color', presetColor);
          syncLastUsedBackgroundColor(selectedClip, draftClips, presetColor);
        }
        break;
      }
      case '[recording] captions enabled changed': {
        draft.contentHub.recordingInfo.captions.enabled = event.payload.enabled;
        // Also sync to per-clip include_captions for API persistence
        syncClipProperty(
          draft.contentHub.recordingInfo.editorState.selectedClip,
          'include_captions',
          event.payload.enabled,
        );
        break;
      }
      case '[recording] captions position changed': {
        syncClipProperty(
          draft.contentHub.recordingInfo.editorState.selectedClip,
          'captions_position',
          event.payload.position,
        );
        break;
      }
      case '[recording] captions active word color changed': {
        const isCustom = event.payload.isCustomActiveWordColor ?? true;
        syncClipProperty(
          draft.contentHub.recordingInfo.editorState.selectedClip,
          'highlight_color',
          event.payload.color,
        );
        syncClipProperty(
          draft.contentHub.recordingInfo.editorState.selectedClip,
          'isCustomActiveWordColor',
          isCustom,
        );
        break;
      }
      case '[recording] captions font style changed': {
        syncClipProperty(
          draft.contentHub.recordingInfo.editorState.selectedClip,
          'captions_style',
          event.payload.fontStyle,
        );
        break;
      }
      case '[recording] aspect ratio changed': {
        // Update aspect ratio on the selected clip
        syncClipProperty(
          draft.contentHub.recordingInfo.editorState.selectedClip,
          'aspect_ratio',
          event.payload.aspect_ratio,
        );
        break;
      }
      case '[recording] edit manual speaker clicked': {
        draft.contentHub.recordingInfo.speakerManagement = {
          editingState: 'edit-speaker',
          speakerKey: event.payload.speakerKey,
          speaker: event.payload.speaker,
          status: 'idle',
        };
        break;
      }
      case '[recording] edit speaker started': {
        draft.contentHub.recordingInfo.speakerManagement.status = 'processing';
        break;
      }
      case '[recording] edit speaker success': {
        if (draft.contentHub.recordingInfo.transcript.data?.speakers) {
          draft.contentHub.recordingInfo.transcript.data.speakers = {
            ...draft.contentHub.recordingInfo.transcript.data.speakers,
            ...event.payload.speakers,
          };
        }
        const updatedSpeaker = event.payload.speakers[event.payload.speakerKey];
        if (
          updatedSpeaker &&
          draft.contentHub.recordingInfo.eventSpeakers.data
        ) {
          const speakerIndex =
            draft.contentHub.recordingInfo.eventSpeakers.data.findIndex(
              speaker =>
                'id' in speaker &&
                'id' in updatedSpeaker &&
                speaker.id === updatedSpeaker.id,
            );
          if (speakerIndex !== -1) {
            draft.contentHub.recordingInfo.eventSpeakers.data[speakerIndex] =
              updatedSpeaker;
          }
        }
        draft.contentHub.recordingInfo.speakerManagement = {
          editingState: 'closed',
          speakerKey: undefined,
          speaker: undefined,
          currentSpeakerName: undefined,
          isUploading: undefined,
          uploadedImageUrl: undefined,
          status: 'idle',
        };
        break;
      }
      case '[recording] edit speaker failed': {
        console.error('Speaker edit failed:', event.payload.error);
        draft.contentHub.recordingInfo.speakerManagement.status = 'failed';
        break;
      }
      case '[recording] fetch content repurposing started': {
        draft.contentHub.creatorLab.contentRepurposing.status = 'processing';
        break;
      }
      case '[recording] initialize creator lab loading phase': {
        draft.contentHub.creatorLab.contentRepurposing.loadingPhase = {
          phase: 'initial',
        };
        break;
      }
      case '[recording] fetch content repurposing success': {
        const response = event.payload;
        const selectedContent = ensureSelectedContent();
        const attributes = selectedContent.attributes;

        if (response.eligibility !== 'ELIGIBLE') {
          attributes.topicId = null;

          draft.contentHub.creatorLab.contentRepurposing = {
            loadingPhase: {
              phase:
                response.eligibility === 'PENDING_CHECKS'
                  ? 'initial'
                  : 'ineligible',
            },
            status: 'success',
            data: response,
          };
          break;
        }

        const { topics, toneOfVoice } = response;
        const isTopicsDone = topics.status === 'FINISHED';
        const isToneOfVoiceDone = toneOfVoice?.status === 'FINISHED';

        if (isTopicsDone) {
          const availableTopics = topics.value ?? [];
          if (availableTopics.length === 0) {
            attributes.topicId = null;
          } else {
            // For EMAIL content type, always use first topic (overall summary) behind the scenes
            // For SOCIAL_POST and BLOG_POST, use selected topic or default to first
            if (isEmailContent(selectedContent)) {
              attributes.topicId = availableTopics[0]?.id ?? null;
            } else if (
              attributes.topicId &&
              availableTopics.some(topic => topic.id === attributes.topicId)
            ) {
              // keep existing topic for SOCIAL_POST and BLOG_POST
            } else {
              attributes.topicId = availableTopics[0]?.id ?? null;
            }
          }
        } else {
          attributes.topicId = null;
        }

        if (isToneOfVoiceDone && toneOfVoice.value) {
          attributes.toneOfVoiceId = toneOfVoice.value;
        }

        const currentPhase =
          draft.contentHub.creatorLab.contentRepurposing.loadingPhase.phase;
        const allDone = isTopicsDone && isToneOfVoiceDone;
        const topicsFailed = topics.status === 'FAILED';
        const toneFailed = !toneOfVoice || toneOfVoice.status === 'FAILED';

        // Scenario 1: Processed recording - both tone and topics are already done
        // Skip directly to lab-page
        if (allDone && currentPhase === 'initial') {
          draft.contentHub.creatorLab.contentRepurposing = {
            loadingPhase: { phase: 'completed' },
            status: 'success',
            data: response,
          };
          break;
        }

        // Scenario 2: Topics failed → Fatal error, cannot proceed
        if (topicsFailed) {
          draft.contentHub.creatorLab.contentRepurposing = {
            loadingPhase: { phase: 'failed' },
            status: 'failed',
            data: response,
            errors: { message: 'content-recording.lab.failed-to-find-topics' },
          };
          break;
        }

        // Scenario 3: Tone failed, but topics succeeded
        // Proceed to lab (the default tone will be selected)
        if (toneFailed && isTopicsDone) {
          draft.contentHub.creatorLab.contentRepurposing = {
            loadingPhase: {
              phase: 'post-processing',
              processingDetails: {
                topics: 'completed',
                toneOfVoice: 'failed',
              },
            },
            status: 'success',
            data: response,
          };
          break;
        }

        // Scenario 4: Tone failed, topics still processing
        // Continue showing progress loader for topics
        if (toneFailed && !isTopicsDone) {
          draft.contentHub.creatorLab.contentRepurposing = {
            loadingPhase: {
              phase: 'post-processing',
              processingDetails: {
                topics: 'processing',
                toneOfVoice: 'failed',
              },
            },
            status: 'success',
            data: response,
          };
          break;
        }

        // Scenario 5: Normal processing (nothing failed)
        // Show step-loader with current completion states
        // When both become completed, ProgressLoader will show completion animation
        // then dispatch 'unmount' action after delay to transition to 'completed'
        draft.contentHub.creatorLab.contentRepurposing = {
          loadingPhase: {
            phase: 'post-processing',
            processingDetails: {
              topics: isTopicsDone ? 'completed' : 'processing',
              toneOfVoice: isToneOfVoiceDone ? 'completed' : 'processing',
            },
          },
          status: 'success',
          data: response,
        };
        break;
      }
      case '[recording] fetch content repurposing failed': {
        draft.contentHub.creatorLab.contentRepurposing = {
          loadingPhase: { phase: 'failed' },
          status: 'failed',
          data: null,
          errors: {
            message: event.payload.error.message,
          },
        };
        resetContentAttributes(ensureSelectedContent());
        break;
      }
      case '[recording] creator lab loading phase completed': {
        draft.contentHub.creatorLab.contentRepurposing.loadingPhase = {
          phase: 'completed',
        };
        break;
      }
      case '[billing] info fetch started': {
        draft.billing.status = 'processing';
        break;
      }
      case '[billing] info fetch success': {
        draft.billing.data = event.payload;
        draft.billing.status = 'success';
        break;
      }
      case '[billing] info fetch failed': {
        draft.billing.status = 'failed';
        break;
      }
      case '[billing] stripe unlink started': {
        draft.billing.stripeUnlink.status = 'processing';
        break;
      }
      case '[billing] stripe unlink success': {
        draft.billing.stripeUnlink.status = 'success';
        break;
      }
      case '[billing] stripe unlink failed': {
        draft.billing.stripeUnlink.status = 'failed';
        break;
      }
      case '[recording] fetch tone of voice config started': {
        draft.contentHub.creatorLab.toneOfVoiceConfig.status = 'processing';
        break;
      }
      case '[recording] fetch tone of voice config success': {
        draft.contentHub.creatorLab.toneOfVoiceConfig = {
          status: 'success',
          data: event.payload.toneOfVoiceOptions,
        };

        const attributes = ensureSelectedContent().attributes;
        const options = event.payload.toneOfVoiceOptions;
        if (!options || options.length === 0) {
          attributes.toneOfVoiceId = null;
        } else if (
          attributes.toneOfVoiceId &&
          options.some(option => option.key === attributes.toneOfVoiceId)
        ) {
          // keep existing tone
        } else {
          attributes.toneOfVoiceId = options[0]?.key ?? null;
        }
        break;
      }
      case '[recording] fetch tone of voice config failed': {
        draft.contentHub.creatorLab.toneOfVoiceConfig = {
          status: 'failed',
          data: null,
          errors: {
            message: event.payload.error.message,
          },
        };
        ensureSelectedContent().attributes.toneOfVoiceId = null;
        break;
      }
      case '[content-composer] fetch content repurposing started': {
        draft.contentHub.contentComposer.contentRepurposing.status =
          'processing';
        break;
      }
      case '[content-composer] fetch content repurposing success': {
        draft.contentHub.contentComposer.contentRepurposing = {
          status: 'success',
          data: event.payload,
        };

        if (
          event.payload.eligibility === 'ELIGIBLE' &&
          event.payload.toneOfVoice &&
          event.payload.toneOfVoice.status === 'FINISHED' &&
          event.payload.toneOfVoice.value
        ) {
          draft.contentHub.contentComposer.generationAttributes.toneOfVoiceId =
            event.payload.toneOfVoice.value;
        } else {
          draft.contentHub.contentComposer.generationAttributes.toneOfVoiceId =
            DEFAULT_TONE_OF_VOICE_ID;
        }

        // Auto-select first topic when topics are available
        if (
          event.payload.eligibility === 'ELIGIBLE' &&
          event.payload.topics.status === 'FINISHED' &&
          event.payload.topics.value &&
          event.payload.topics.value.length > 0
        ) {
          const firstTopic = event.payload.topics.value[0];
          // Set topic if not already selected
          if (
            draft.contentHub.contentComposer.generationAttributes.topicId ===
            null
          ) {
            draft.contentHub.contentComposer.generationAttributes.topicId =
              firstTopic.id;
          }
        }
        break;
      }
      case '[content-composer] fetch content repurposing failed': {
        draft.contentHub.contentComposer.contentRepurposing = {
          status: 'failed',
          data: null,
          errors: {
            message: event.payload.error.message,
          },
        };
        break;
      }
      case '[content-composer] fetch tone of voice config started': {
        draft.contentHub.contentComposer.toneOfVoiceConfig.status =
          'processing';
        break;
      }
      case '[content-composer] fetch tone of voice config success': {
        draft.contentHub.contentComposer.toneOfVoiceConfig = {
          status: 'success',
          data: event.payload.toneOfVoiceOptions,
        };

        break;
      }
      case '[content-composer] fetch tone of voice config failed': {
        draft.contentHub.contentComposer.toneOfVoiceConfig = {
          status: 'failed',
          data: null,
          errors: {
            message: event.payload.error.message,
          },
        };
        break;
      }
      case '[content-composer] fetch email config started': {
        draft.contentHub.contentComposer.emailAudienceConfig.status =
          'processing';
        break;
      }
      case '[content-composer] fetch email config success': {
        const { emailAudience } = event.payload as {
          emailAudience: Array<{
            key: string;
            labelKey: string;
            types: Array<{ key: string; labelKey: string }>;
          }>;
        };
        draft.contentHub.contentComposer.emailAudienceConfig = {
          status: 'success',
          data: emailAudience,
        };

        // Auto-select first email audience and type for Content Composer
        // Email config always loads before user interaction, so we can always set defaults
        if (emailAudience.length > 0) {
          const firstAudience = emailAudience[0];
          const firstType = firstAudience.types?.[0];
          if (firstAudience && firstType) {
            draft.contentHub.contentComposer.generationAttributes.emailAudienceId =
              firstAudience.key;
            draft.contentHub.contentComposer.generationAttributes.emailTypeId =
              firstType.key;
          }
        }
        break;
      }
      case '[content-composer] fetch email config failed': {
        draft.contentHub.contentComposer.emailAudienceConfig = {
          status: 'failed',
          data: null,
          errors: {
            message: event.payload.error.message,
          },
        };
        break;
      }
      case '[content-composer] tone selected': {
        const toneOptions =
          draft.contentHub.contentComposer.toneOfVoiceConfig.data ?? [];
        const availableTone =
          toneOptions.find(option => option.key === event.payload.toneId) ??
          toneOptions[0];
        draft.contentHub.contentComposer.generationAttributes.toneOfVoiceId =
          availableTone?.key ?? null;
        break;
      }
      case '[content-composer] fetch content history started': {
        const { contentType } = event.payload as {
          contentType: ContentType;
        };
        const history =
          draft.contentHub.contentComposer.contentHistory[
            contentType as ContentType
          ];
        if (history) {
          history.status = 'processing';
        }
        break;
      }
      case '[content-composer] fetch content history success': {
        const { contentType, response } = event.payload;

        const history =
          draft.contentHub.contentComposer.contentHistory[
            contentType as ContentType
          ];
        if (history) {
          // Store enriched items directly from the V2 API response
          history.history = response.data;
          history.cursor = response.cursor;
          history.total = response.total;
          history.status = 'success';
        }
        break;
      }
      case '[content-composer] fetch content history failed': {
        const { contentType, error } = event.payload as {
          contentType: ContentType;
          error: Error;
        };
        const history =
          draft.contentHub.contentComposer.contentHistory[
            contentType as ContentType
          ];
        if (history) {
          history.history = [];
          history.cursor = null;
          history.total = 0;
          history.status = 'failed';
          (history as any).errors = {
            message: error.message,
          };
        }
        break;
      }
      case '[content-composer] fetch content history next page started': {
        const { contentType } = event.payload as {
          contentType: ContentType;
        };
        const history =
          draft.contentHub.contentComposer.contentHistory[
            contentType as ContentType
          ];
        if (history) {
          history.status = 'processing';
        }
        break;
      }
      case '[content-composer] fetch content history next page success': {
        const { contentType, response } = event.payload;

        const history =
          draft.contentHub.contentComposer.contentHistory[
            contentType as ContentType
          ];
        if (history) {
          // Append new items to existing history
          history.history = [...history.history, ...response.data];
          history.cursor = response.cursor;
          // Total remains the same (from initial fetch)
          history.status = 'success';
        }
        break;
      }
      case '[content-composer] fetch content history next page failed': {
        const { contentType } = event.payload as {
          contentType: ContentType;
        };
        const history =
          draft.contentHub.contentComposer.contentHistory[
            contentType as ContentType
          ];
        if (history) {
          // Keep existing history, just mark as failed and allow retry
          history.status = 'failed';
        }
        break;
      }
      case '[content-composer] feedback changed': {
        // TODO: Add saga to submit feedback to API
        // Currently only updates local state
        const { contentType, contentId, optionIndex, feedbackType } =
          event.payload;

        // Initialize feedback structure if it doesn't exist
        if (!draft.contentHub.contentComposer.feedback[contentType]) {
          draft.contentHub.contentComposer.feedback[contentType] = {};
        }

        if (
          !draft.contentHub.contentComposer.feedback[contentType][contentId]
        ) {
          draft.contentHub.contentComposer.feedback[contentType][contentId] = {
            options: {},
          };
        }

        // Update feedback for the specific option
        draft.contentHub.contentComposer.feedback[contentType][
          contentId
        ].options[optionIndex] = feedbackType;
        break;
      }
      case '[content-composer] topic selected': {
        const { topicId } = event.payload as {
          contentType: ContentType;
          topicId: string;
        };
        draft.contentHub.contentComposer.generationAttributes.topicId = topicId;
        break;
      }
      case '[content-composer] email audience selected': {
        const { audienceId } = event.payload as { audienceId: string };
        const audienceOptions =
          draft.contentHub.contentComposer.emailAudienceConfig.data ?? [];
        const matchingAudience = audienceOptions.find(
          option => option.key === audienceId,
        );

        draft.contentHub.contentComposer.generationAttributes.emailAudienceId =
          audienceId;
        draft.contentHub.contentComposer.generationAttributes.emailTypeId =
          matchingAudience?.types[0]?.key ?? null;
        break;
      }
      case '[content-composer] email type selected': {
        const { emailTypeId } = event.payload as { emailTypeId: string };
        draft.contentHub.contentComposer.generationAttributes.emailTypeId =
          emailTypeId;
        break;
      }
      // ========================================================================
      // Content Composer - Fetch Content by ID
      // ========================================================================
      case '[content-composer] fetch content by id started': {
        const { contentId } = event.payload as { contentId: string };
        draft.contentHub.contentComposer.generatedContent[contentId] = {
          fetchStatus: 'loading',
          content: null,
        };
        break;
      }
      case '[content-composer] fetch content by id success': {
        const { contentId, content, status, contentType } = event.payload as {
          contentId: string;
          content: { options: string[] } | null;
          status: ContentRepurposingStatus;
          contentType: ContentType;
        };
        // Update the generated content cache
        // Content is null when generation status is FAILED
        draft.contentHub.contentComposer.generatedContent[contentId] = {
          fetchStatus: 'fetched',
          content,
        };
        // Propagate API status to the history item (single source of truth)
        const history =
          draft.contentHub.contentComposer.contentHistory[contentType];
        if (history) {
          const historyItem = history.history.find(h => h.id === contentId);
          if (historyItem) {
            historyItem.status = status;
          }
        }
        break;
      }
      case '[content-composer] fetch content by id failed': {
        const { contentId, error } = event.payload as {
          contentId: string;
          error: string;
        };
        draft.contentHub.contentComposer.generatedContent[contentId] = {
          fetchStatus: 'failed',
          content: null,
          error,
        };
        break;
      }
      case '[content-composer] fetch content by id reset': {
        const { contentId } = event.payload as { contentId: string };
        draft.contentHub.contentComposer.generatedContent[contentId] = {
          fetchStatus: 'idle',
          content: null,
        };
        break;
      }
      case '[content-composer] clear generated content cache': {
        // TODO: Consider implementing LRU cache or automatic cleanup
        // to prevent unbounded growth with many regenerations
        draft.contentHub.contentComposer.generatedContent = {};
        break;
      }
      case '[content-composer] request content generation started': {
        draft.contentHub.contentComposer.isContentGenerationRequestInProgress =
          true;
        break;
      }
      case '[content-composer] request content generation success': {
        draft.contentHub.contentComposer.isContentGenerationRequestInProgress =
          false;
        break;
      }
      case '[content-composer] request content generation failed': {
        draft.contentHub.contentComposer.isContentGenerationRequestInProgress =
          false;
        break;
      }
      case '[content-composer] add content to history': {
        const { contentType, item } = event.payload as {
          contentType: ContentType;
          item: ContentHistoryItem;
        };
        const history =
          draft.contentHub.contentComposer.contentHistory[
            contentType as ContentType
          ];
        if (history) {
          // Prepend new item to beginning (latest first)
          history.history.unshift(item);
          // Increment total count
          history.total += 1;
        }
        break;
      }
      case '[content-composer] refetch content repurposing': {
        // Reset content repurposing state to idle to trigger refetch
        draft.contentHub.contentComposer.contentRepurposing.status = 'idle';
        break;
      }
      case '[content-composer] update content history item status': {
        const { contentType, contentId, status } = event.payload as {
          contentType: ContentType;
          contentId: string;
          status: ContentRepurposingStatus;
        };
        const history =
          draft.contentHub.contentComposer.contentHistory[
            contentType as ContentType
          ];
        if (history?.history) {
          const item = history.history.find(h => h.id === contentId);
          if (item) {
            item.status = status;
          }
        }
        break;
      }
      case '[content-composer] invalidate content cache': {
        const { contentId } = event.payload as { contentId: string };
        // Remove the content from cache to force refetch on next access
        delete draft.contentHub.contentComposer.generatedContent[contentId];
        break;
      }
      case '[content-composer] saga initialized': {
        draft.contentHub.contentComposer.isInitialized = true;
        break;
      }
      case '[recording] fetch email config started': {
        draft.contentHub.creatorLab.emailAudienceConfig.status = 'processing';
        break;
      }
      case '[recording] fetch email config success': {
        draft.contentHub.creatorLab.emailAudienceConfig = {
          status: 'success',
          data: event.payload.emailAudience,
        };

        const selectedContent = ensureSelectedContent();
        if (!isEmailContent(selectedContent)) {
          break;
        }

        const attributes = selectedContent.attributes;
        const audienceOptions = event.payload.emailAudience ?? [];
        if (audienceOptions.length === 0) {
          attributes.emailAudienceId = null;
          attributes.emailTypeId = null;
          break;
        }

        const currentAudience = attributes.emailAudienceId;
        const matchingAudience = currentAudience
          ? audienceOptions.find(option => option.key === currentAudience)
          : undefined;

        if (matchingAudience) {
          attributes.emailAudienceId = matchingAudience.key;
          const currentEmailType = attributes.emailTypeId;
          const emailTypeExists = currentEmailType
            ? matchingAudience.types.some(
                typeOption => typeOption.key === currentEmailType,
              )
            : false;
          attributes.emailTypeId = emailTypeExists
            ? currentEmailType
            : matchingAudience.types[0]?.key ?? null;
        } else {
          const fallbackAudience = audienceOptions[0];
          attributes.emailAudienceId = fallbackAudience?.key ?? null;
          attributes.emailTypeId = fallbackAudience?.types[0]?.key ?? null;
        }
        break;
      }
      case '[recording] fetch email config failed': {
        draft.contentHub.creatorLab.emailAudienceConfig = {
          status: 'failed',
          data: null,
          errors: {
            message: event.payload.error.message,
          },
        };
        const selectedContent = ensureSelectedContent();
        if (!isEmailContent(selectedContent)) {
          break;
        }

        const attributes = selectedContent.attributes;
        attributes.emailAudienceId = null;
        attributes.emailTypeId = null;
        break;
      }
      case '[recording] fetch content history started': {
        const { contentType } = event.payload as {
          contentType: ContentType;
        };
        const history =
          draft.contentHub.creatorLab.contentHistory[
            contentType as ContentType
          ];
        if (history) {
          history.status = 'processing';
        }
        break;
      }
      case '[recording] fetch content history success': {
        const { contentType, response } = event.payload as {
          contentType: ContentType;
          response: {
            data: Array<{ id: string; status: string; createdAt: string }>;
            cursor: string | null;
          };
        };

        const historyEntries: ContentHistoryEntry[] = response.data.map(
          (item: { id: string; status: string; createdAt: string }) => ({
            id: item.id,
            status: mapApiStatusToFrontend(item.status as any),
            createdAt: item.createdAt,
          }),
        );

        const history =
          draft.contentHub.creatorLab.contentHistory[
            contentType as ContentType
          ];
        if (history) {
          history.history = historyEntries;
          history.cursor = response.cursor;
          history.status = 'success';
        }
        break;
      }
      case '[recording] fetch content history failed': {
        const { contentType, error } = event.payload as {
          contentType: ContentType;
          error: Error;
        };
        const history =
          draft.contentHub.creatorLab.contentHistory[
            contentType as ContentType
          ];
        if (history) {
          history.history = [];
          history.cursor = null;
          history.status = 'failed';
          (history as any).errors = {
            message: error.message,
          };
        }
        break;
      }
      case '[recording] optimistically add content to history': {
        const { contentType, contentId } = event.payload as {
          contentType: ContentType;
          contentId: string;
        };
        const history =
          draft.contentHub.creatorLab.contentHistory[
            contentType as ContentType
          ];
        if (history) {
          // Add optimistic entry at the beginning (latest)
          history.history.unshift({
            id: contentId,
            status: 'initial' as const,
            createdAt: new Date().toISOString(),
          });
          history.status = 'processing';
        }

        // Create temporary GeneratedContent entry for optimistic state
        // This allows the UI to show loading state immediately
        if (!draft.contentHub.creatorLab.generatedContent) {
          draft.contentHub.creatorLab.generatedContent = {};
        }
        draft.contentHub.creatorLab.generatedContent[contentId] = {
          status: 'initial',
          topics: [],
          toneVoiceId: '',
          contentType: contentType,
          context: {},
          content: '',
        } as GeneratedContent & { status: 'processing' };
        break;
      }
      case '[recording] add content to history': {
        const { contentType, contentId, status } = event.payload as {
          contentType: ContentType;
          contentId: string;
          status: ContentGenerationStatus;
        };
        const history =
          draft.contentHub.creatorLab.contentHistory[
            contentType as ContentType
          ];
        if (history) {
          // Check if entry already exists (avoid duplicates)
          const existingIndex = history.history.findIndex(
            entry => entry.id === contentId,
          );
          if (existingIndex >= 0) {
            // Update existing entry status
            history.history[existingIndex].status = status;
          } else {
            // Add new entry at the beginning (latest)
            history.history.unshift({
              id: contentId,
              status,
              createdAt: new Date().toISOString(),
            });
          }
          // Ensure history status is success if it was idle
          if (history.status === 'idle') {
            (history as any).status = 'success';
          }
        }
        break;
      }
      case '[recording] remove optimistic content from history': {
        const { contentType } = event.payload as {
          contentType: ContentType;
        };
        const history =
          draft.contentHub.creatorLab.contentHistory?.[
            contentType as ContentType
          ];
        if (history && history.history.length > 0) {
          // Remove the first entry if it has optimistic ID or is in initial state
          const firstEntry = history.history[0];
          if (
            firstEntry.id === OPTIMISTIC_CONTENT_ID ||
            firstEntry.status === 'initial'
          ) {
            history.history.shift();

            // Also remove the temporary GeneratedContent entry
            if (
              draft.contentHub.creatorLab.generatedContent &&
              draft.contentHub.creatorLab.generatedContent[firstEntry.id]
            ) {
              delete draft.contentHub.creatorLab.generatedContent[
                firstEntry.id
              ];
            }
          }
        }
        break;
      }
      case '[recording] fetch content started': {
        const { contentId } = event.payload as { contentId: string };
        if (!draft.contentHub.creatorLab.generatedContent) {
          draft.contentHub.creatorLab.generatedContent = {};
        }
        const contentItem =
          draft.contentHub.creatorLab.generatedContent[contentId];
        if (!contentItem) {
          draft.contentHub.creatorLab.generatedContent[contentId] = {
            status: 'initial',
            topics: [],
            toneVoiceId: '',
            contentType: '',
            context: {},
            content: '',
          } as GeneratedContent & { status: 'processing' };
        } else {
          (contentItem as any).status = 'processing';
        }
        break;
      }
      case '[recording] fetch content success': {
        const { contentId, response } = event.payload as {
          contentId: string;
          response: {
            status: string;
            topics: string[];
            toneOfVoice: string | null;
            contentType: string;
            context?: Record<string, unknown>;
            content: { options: string[] } | null;
            error?: string;
          };
        };

        if (!draft.contentHub.creatorLab.generatedContent) {
          draft.contentHub.creatorLab.generatedContent = {};
        }

        const generatedContent: GeneratedContent = {
          status: mapApiStatusToFrontend(
            response.status as ApiContentRepurposingStatus,
          ),
          topics: response.topics,
          toneVoiceId: response.toneOfVoice ?? '',
          contentType: response.contentType,
          context: (response.context as Record<string, string>) ?? {},
          content: response.content,
          error: response.status === 'FAILED' ? response.error : undefined,
        };

        draft.contentHub.creatorLab.generatedContent[contentId] = {
          ...generatedContent,
        } as GeneratedContent & { status: 'success' };

        // Update selectedContent attributes to match the fetched content
        // This ensures UI selectors reflect the actual content attributes
        const selectedContent = ensureSelectedContent();

        // Set isContentLoading based on content status and availability
        const mappedStatus = mapApiStatusToFrontend(
          response.status as ApiContentRepurposingStatus,
        );
        if (selectedContent.id === contentId) {
          if (
            mappedStatus === 'finished' &&
            response.content &&
            response.content.options &&
            response.content.options.length > 0
          ) {
            draft.contentHub.creatorLab.isContentLoading = false;
          } else if (
            mappedStatus === 'initial' ||
            mappedStatus === 'in_progress'
          ) {
            draft.contentHub.creatorLab.isContentLoading = true;
          }
        }
        if (selectedContent.id === contentId) {
          // Update tone of voice
          selectedContent.attributes.toneOfVoiceId = response.toneOfVoice;

          // Update topic: for EMAIL use first topic, for others use first topic from array
          if (response.topics.length > 0) {
            selectedContent.attributes.topicId = response.topics[0];
          }

          // Update email attributes if this is EMAIL content
          if (isEmailContent(selectedContent) && response.context) {
            const context = response.context as Record<string, string>;
            selectedContent.attributes.emailAudienceId =
              context.recipientTypeId ?? null;
            selectedContent.attributes.emailTypeId =
              context.emailTypeId ?? null;
          }
        }
        break;
      }
      case '[recording] invalidate content cache': {
        const { contentId } = event.payload as {
          contentId: string;
        };
        if (draft.contentHub.creatorLab.generatedContent?.[contentId]) {
          delete draft.contentHub.creatorLab.generatedContent[contentId];
        }
        break;
      }
      case '[recording] fetch content failed': {
        const { contentId, error } = event.payload as {
          contentId: string;
          error: Error;
        };
        if (!draft.contentHub.creatorLab.generatedContent) {
          draft.contentHub.creatorLab.generatedContent = {};
        }
        const contentItem =
          draft.contentHub.creatorLab.generatedContent[contentId];
        if (!contentItem) {
          const failedContent = {
            status: 'initial' as const,
            topics: [],
            toneVoiceId: '',
            contentType: '',
            context: {},
            content: null,
          } as GeneratedContent;
          draft.contentHub.creatorLab.generatedContent[contentId] = {
            ...failedContent,
            status: 'failed',
            errors: {
              message: error.message,
            },
          } as any;
        } else {
          (contentItem as any).status = 'failed';
          (contentItem as any).errors = {
            message: error.message,
          };
        }
        break;
      }
      case '[recording] select content': {
        const { contentId, contentType } = event.payload as {
          contentId: string;
          contentType: ContentType;
        };

        const selectedContent = ensureSelectedContent();
        selectedContent.id = contentId;
        selectedContent.type = contentType;

        // If content is already cached, update attributes to match cached content
        const cachedContent =
          draft.contentHub.creatorLab.generatedContent?.[contentId];
        if (cachedContent && 'topics' in cachedContent) {
          const content = cachedContent as GeneratedContent;
          // Update tone of voice
          selectedContent.attributes.toneOfVoiceId =
            content.toneVoiceId || null;

          // Update topic: use first topic from array
          if (content.topics.length > 0) {
            selectedContent.attributes.topicId = content.topics[0];
          }

          // Update email attributes if this is EMAIL content
          if (isEmailContent(selectedContent) && content.context) {
            selectedContent.attributes.emailAudienceId =
              content.context.recipientTypeId ?? null;
            selectedContent.attributes.emailTypeId =
              content.context.emailTypeId ?? null;
          }

          // Update isContentLoading based on content status and availability
          if (
            content.status === 'finished' &&
            content.content &&
            content.content.options &&
            content.content.options.length > 0
          ) {
            draft.contentHub.creatorLab.isContentLoading = false;
          } else if (
            content.status === 'initial' ||
            content.status === 'in_progress'
          ) {
            draft.contentHub.creatorLab.isContentLoading = true;
          }
        } else {
          draft.contentHub.creatorLab.isContentLoading = true;
        }
        break;
      }
      case '[recording] request content generation started': {
        // Track that a request is live using a flag
        draft.contentHub.creatorLab.isContentGenerationRequestInProgress = true;
        draft.contentHub.creatorLab.isContentLoading = true;
        break;
      }
      case '[recording] request content generation success': {
        // Request completed, clear the flag
        draft.contentHub.creatorLab.isContentGenerationRequestInProgress =
          false;
        break;
      }
      case '[recording] request content generation failed': {
        // Request failed, clear the flag
        draft.contentHub.creatorLab.isContentGenerationRequestInProgress =
          false;
        break;
      }
      case '[recording] content generation tone selected': {
        const toneOptions =
          draft.contentHub.creatorLab.toneOfVoiceConfig.data ?? [];
        const availableTone =
          toneOptions.find(option => option.key === event.payload.toneId) ??
          toneOptions[0];
        ensureSelectedContent().attributes.toneOfVoiceId =
          availableTone?.key ?? null;
        break;
      }
      case '[recording] content generation audience selected': {
        const audienceOptions =
          draft.contentHub.creatorLab.emailAudienceConfig.data ?? [];
        const matchingAudience =
          audienceOptions.find(
            option => option.key === event.payload.audienceId,
          ) ?? audienceOptions[0];

        const selectedContent = ensureSelectedContent();
        if (!isEmailContent(selectedContent)) {
          break;
        }

        const attributes = selectedContent.attributes;
        if (!matchingAudience) {
          attributes.emailAudienceId = null;
          attributes.emailTypeId = null;
          break;
        }

        attributes.emailAudienceId = matchingAudience.key;
        attributes.emailTypeId = matchingAudience.types[0]?.key ?? null;
        break;
      }
      case '[recording] content generation email type selected': {
        const audienceOptions =
          draft.contentHub.creatorLab.emailAudienceConfig.data ?? [];
        const selectedContent = ensureSelectedContent();
        if (!isEmailContent(selectedContent)) {
          break;
        }

        const attributes = selectedContent.attributes;
        const selectedAudience = attributes.emailAudienceId;

        if (!selectedAudience) {
          attributes.emailTypeId = null;
          break;
        }

        const audience = audienceOptions.find(
          option => option.key === selectedAudience,
        );

        if (!audience) {
          const fallbackAudience = audienceOptions[0];
          attributes.emailAudienceId = fallbackAudience?.key ?? null;
          attributes.emailTypeId = fallbackAudience?.types[0]?.key ?? null;
          break;
        }

        const matchingType =
          audience.types.find(
            typeOption => typeOption.key === event.payload.emailTypeId,
          ) ?? audience.types[0];

        attributes.emailTypeId = matchingType?.key ?? null;
        break;
      }
      case '[recording] content generation topic selected': {
        const contentRepurposing =
          draft.contentHub.creatorLab.contentRepurposing.data;
        if (
          !contentRepurposing ||
          contentRepurposing.eligibility !== 'ELIGIBLE' ||
          contentRepurposing.topics.status !== 'FINISHED'
        ) {
          ensureSelectedContent().attributes.topicId = null;
          break;
        }

        const topics = contentRepurposing.topics.value ?? [];
        const selectedTopic =
          topics.find(topic => topic.id === event.payload.topicId) ?? topics[0];

        ensureSelectedContent().attributes.topicId = selectedTopic?.id ?? null;
        break;
      }
      case '[recording] fetch config success': {
        draft.contentHub.config.status = 'success';
        draft.contentHub.config.hasContentHubCreatorLabAccess =
          event.payload.hasContentHubCreatorLabAccess;
        draft.contentHub.config.hasContentHubAccess =
          event.payload.hasContentHubAccess;
        break;
      }
      case '[recording] fetch config failed': {
        draft.contentHub.config.status = 'failed';
        draft.contentHub.config.hasContentHubCreatorLabAccess = false;
        draft.contentHub.config.hasContentHubAccess = false;
        break;
      }
      // Advanced Editor Reducer Cases (Phase 1.1 + Option 2: Flexible Undo)
      case '[advanced-editor] edit selected': {
        const advancedEdits = draft.contentHub.recordingInfo.advancedEdits;
        const selectedEditId = event.payload.editId;
        advancedEdits.activeEditId = selectedEditId;

        if (!advancedEdits.editEntitiesById[selectedEditId]) {
          const selectedRow = advancedEdits.edits.find(
            edit => edit.id === selectedEditId,
          );
          const recording = draft.contentHub.recordingInfo.data;
          const recordingId = recording?.id ?? selectedRow?.id ?? 'recording';
          const recordingDurationSec = recording?.duration ?? 0;
          const hydratedCuts = hydrateAdvancedEditCutsFromRow({
            selectedEditId,
            recordingId,
            recordingDurationSec,
            rowCuts: selectedRow?.cuts,
          });

          advancedEdits.editEntitiesById[selectedEditId] = {
            id: selectedEditId,
            status: 'DRAFT',
            revision: 0,
            createdAt: selectedRow?.createdAt,
            draft: {
              title: selectedRow?.title ?? 'Untitled Edit',
              cuts: hydratedCuts,
              captions: { enabled: false },
              aspectRatio:
                selectedRow?.aspectRatio === '9_16' ||
                selectedRow?.aspectRatio === '1_1'
                  ? selectedRow.aspectRatio
                  : '16_9',
              backgroundColor: null,
              wordCorrections: [],
            },
          };
        }
        break;
      }
      case '[advanced-editor] edit deselected': {
        const advancedEdits = draft.contentHub.recordingInfo.advancedEdits;
        // Reset the fetch status for the departing edit so the de-duplication
        // guard in fetchRecordingEditById allows a fresh GET on the next visit.
        // Without this, 'success' would persist for the whole recording session
        // and external changes (another tab, a completed export) would never be
        // picked up until a full page reload.
        if (advancedEdits.activeEditId) {
          delete advancedEdits.editFetchStatusById[advancedEdits.activeEditId];
        }
        advancedEdits.activeEditId = null;
        advancedEdits.status = 'idle';
        break;
      }
      case '[advanced-editor] edit draft changed': {
        const { editId, draft: newDraft, previousDraft } = event.payload;
        const advancedEdits = draft.contentHub.recordingInfo.advancedEdits;

        // Increment version for this edit
        advancedEdits.editVersionByEditId[editId] =
          (advancedEdits.editVersionByEditId[editId] ?? 0) + 1;

        const entity = advancedEdits.editEntitiesById[editId];
        if (entity) {
          entity.draft = newDraft;
        } else {
          advancedEdits.editEntitiesById[editId] = {
            id: editId,
            status: 'DRAFT',
            revision: 0,
            draft: newDraft,
          };
        }

        // Push previous draft to undo stack if provided
        if (previousDraft) {
          if (!advancedEdits.historyByEditId[editId]) {
            advancedEdits.historyByEditId[editId] = { undo: [], redo: [] };
          }
          const history = advancedEdits.historyByEditId[editId];
          history.undo.unshift(previousDraft);
          // Cap undo stack at MAX_HISTORY_SIZE
          if (history.undo.length > MAX_HISTORY_SIZE) {
            history.undo = history.undo.slice(0, MAX_HISTORY_SIZE);
          }
          // Clear redo stack on new edit (Decision 1 pattern)
          history.redo = [];
        }

        // Mark as idle, but preserve checkpoint info (Option 2)
        const currentCheckpoint = advancedEdits.persistByEditId[editId];
        advancedEdits.persistByEditId[editId] = {
          status: 'idle',
          lastSavedVersion: currentCheckpoint?.lastSavedVersion ?? 0,
          lastSavedDraft: currentCheckpoint?.lastSavedDraft,
          lastSavedAt: currentCheckpoint?.lastSavedAt,
        };
        break;
      }
      case '[advanced-editor] undo': {
        const { editId } = event.payload;
        const advancedEdits = draft.contentHub.recordingInfo.advancedEdits;
        const history = advancedEdits.historyByEditId[editId];

        if (!history || history.undo.length === 0) break; // No undo available

        const prevDraft = history.undo.shift();
        if (!prevDraft) break;

        const edit = advancedEdits.editEntitiesById[editId];
        if (!edit) break;

        // Move current draft to redo stack
        history.redo.unshift(edit.draft);

        // Update edit with previous draft
        edit.draft = prevDraft;

        // Increment version so hasUnsavedChanges reflects the new draft state
        advancedEdits.editVersionByEditId[editId] =
          (advancedEdits.editVersionByEditId[editId] ?? 0) + 1;
        break;
      }
      case '[advanced-editor] redo': {
        const { editId } = event.payload;
        const advancedEdits = draft.contentHub.recordingInfo.advancedEdits;
        const history = advancedEdits.historyByEditId[editId];

        if (!history || history.redo.length === 0) break; // No redo available

        const nextDraft = history.redo.shift();
        if (!nextDraft) break;

        const edit = advancedEdits.editEntitiesById[editId];
        if (!edit) break;

        // Move current draft to undo stack
        history.undo.unshift(edit.draft);

        // Update edit with next draft
        edit.draft = nextDraft;

        // Increment version so hasUnsavedChanges reflects the new draft state
        advancedEdits.editVersionByEditId[editId] =
          (advancedEdits.editVersionByEditId[editId] ?? 0) + 1;
        break;
      }
      case '[advanced-editor] clear redo stack': {
        const { editId } = event.payload;
        const advancedEdits = draft.contentHub.recordingInfo.advancedEdits;

        if (!advancedEdits.historyByEditId[editId]) {
          advancedEdits.historyByEditId[editId] = { undo: [], redo: [] };
        }
        advancedEdits.historyByEditId[editId].redo = [];
        break;
      }
      case '[advanced-editor] auto-save started': {
        const { editId, draft: savingDraft } = event.payload;
        const advancedEdits = draft.contentHub.recordingInfo.advancedEdits;
        const currentCheckpoint = advancedEdits.persistByEditId[editId];

        // Record that we're saving and which version we're saving
        advancedEdits.persistByEditId[editId] = {
          status: 'saving',
          lastSavedVersion: currentCheckpoint?.lastSavedVersion ?? 0,
          lastSavedDraft: currentCheckpoint?.lastSavedDraft ?? savingDraft,
          lastSavedAt: currentCheckpoint?.lastSavedAt,
        };
        break;
      }
      case '[advanced-editor] auto-save success': {
        const { editId, newRevision, updatedAt } = event.payload;
        const advancedEdits = draft.contentHub.recordingInfo.advancedEdits;
        const currentVersion = advancedEdits.editVersionByEditId[editId] ?? 0;
        const edit = advancedEdits.editEntitiesById[editId];

        if (edit) {
          // Bump revision so the next PUT sends the correct concurrency token.
          edit.revision = newRevision;
          // Store server-confirmed save time for the "Saved at <time>" tooltip.
          edit.updatedAt = updatedAt;

          const listRow = advancedEdits.edits.find(row => row.id === editId);
          if (listRow) {
            listRow.cuts =
              edit.draft.cuts.length > 0
                ? editListCutsFromDraft(edit.draft.cuts)
                : undefined;
          }
        }

        // Mark checkpoint: save succeeded at this version. failureReason is
        // intentionally omitted — clearing it signals the save is clean.
        advancedEdits.persistByEditId[editId] = {
          status: 'saved',
          lastSavedVersion: currentVersion,
          lastSavedDraft: edit?.draft,
          lastSavedAt: new Date().toISOString(),
        };
        break;
      }
      case '[advanced-editor] auto-save conflict': {
        // A 409 from the PUT endpoint. The saga has already shown a toast
        // telling the user to reload. Mark persist as failed so subsequent
        // auto-save attempts are suppressed until the user reloads.
        const { editId, failureReason } = event.payload;
        const advancedEdits = draft.contentHub.recordingInfo.advancedEdits;
        const currentCheckpoint = advancedEdits.persistByEditId[editId];

        advancedEdits.persistByEditId[editId] = {
          status: 'failed',
          failureReason,
          lastSavedVersion: currentCheckpoint?.lastSavedVersion ?? 0,
          lastSavedDraft: currentCheckpoint?.lastSavedDraft,
          lastSavedAt: currentCheckpoint?.lastSavedAt,
        };
        break;
      }
      case '[advanced-editor] auto-save failed': {
        const { editId } = event.payload;
        const advancedEdits = draft.contentHub.recordingInfo.advancedEdits;
        const currentCheckpoint = advancedEdits.persistByEditId[editId];

        advancedEdits.persistByEditId[editId] = {
          status: 'failed',
          failureReason: 'generic',
          lastSavedVersion: currentCheckpoint?.lastSavedVersion ?? 0,
          lastSavedDraft: currentCheckpoint?.lastSavedDraft,
          lastSavedAt: currentCheckpoint?.lastSavedAt,
        };
        break;
      }
      case '[advanced-editor] fetch edits started': {
        draft.contentHub.recordingInfo.advancedEdits.listStatus = 'loading';
        break;
      }
      case '[advanced-editor] fetch edits success': {
        const { edits: fetchedEdits, total, cursor, append } = event.payload;
        const advancedEdits = draft.contentHub.recordingInfo.advancedEdits;
        // append: later pages only contain the next slice — merge and dedupe by id so we keep prior rows.
        if (append) {
          const seen = new Set(advancedEdits.edits.map(e => e.id));
          for (const item of fetchedEdits) {
            if (!seen.has(item.id)) {
              advancedEdits.edits.push(item);
              seen.add(item.id);
            }
          }
        } else {
          advancedEdits.edits = fetchedEdits;
          // Drop full entities that are no longer present in the latest list response.
          const keepIds = new Set(fetchedEdits.map(e => e.id));
          for (const id of Object.keys(advancedEdits.editEntitiesById)) {
            if (!keepIds.has(id)) {
              delete advancedEdits.editEntitiesById[id];
            }
          }
        }
        advancedEdits.listTotal = total;
        advancedEdits.listCursor = cursor;
        advancedEdits.listStatus = 'success';
        if (
          advancedEdits.activeEditId &&
          !advancedEdits.edits.some(e => e.id === advancedEdits.activeEditId)
        ) {
          advancedEdits.activeEditId = null;
        }
        break;
      }
      case '[advanced-editor] fetch edits failed': {
        draft.contentHub.recordingInfo.advancedEdits.listStatus = 'failed';
        break;
      }
      case '[advanced-editor] create edit started': {
        draft.contentHub.recordingInfo.advancedEdits.status = 'processing';
        break;
      }
      case '[advanced-editor] create edit success': {
        const advancedEdits = draft.contentHub.recordingInfo.advancedEdits;
        advancedEdits.status = 'success';
        if (event.payload.editId) {
          advancedEdits.activeEditId = event.payload.editId;
        }
        break;
      }
      case '[advanced-editor] create edit failed': {
        draft.contentHub.recordingInfo.advancedEdits.status = 'failed';
        break;
      }
      case '[advanced-editor] fetch edit by id started': {
        const { editId } = event.payload;
        draft.contentHub.recordingInfo.advancedEdits.editFetchStatusById[
          editId
        ] = 'loading';
        break;
      }
      case '[advanced-editor] fetch edit by id success': {
        const { editId, entity } = event.payload;
        const advancedEdits = draft.contentHub.recordingInfo.advancedEdits;
        // Replace any placeholder (created by 'edit selected') with the full
        // entity from the API, which includes captions, backgroundColor,
        // revision, status and hasUnexportedChanges.
        advancedEdits.editEntitiesById[editId] = entity;
        advancedEdits.editFetchStatusById[editId] = 'success';
        break;
      }
      case '[advanced-editor] fetch edit by id failed': {
        const { editId } = event.payload;
        draft.contentHub.recordingInfo.advancedEdits.editFetchStatusById[
          editId
        ] = 'failed';
        break;
      }

      // ── Legacy clip migration ──────────────────────────────────────────────
      case '[advanced-editor] legacy clip convert started':
      case '[advanced-editor] legacy clip discard started': {
        // Optimistic remove — clip disappears from the legacy list immediately.
        // Re-added on the corresponding *failed* event if the API call errors.
        const { clipId } = event.payload;
        draft.contentHub.recordingInfo.editorState.draft_clips =
          draft.contentHub.recordingInfo.editorState.draft_clips.filter(
            c => c.id !== clipId,
          );
        break;
      }
      case '[advanced-editor] legacy clip convert failed':
      case '[advanced-editor] legacy clip discard failed': {
        // Re-add the clip if the API call failed so the user can retry.
        const { clip } = event.payload;
        const clips = draft.contentHub.recordingInfo.editorState.draft_clips;
        if (!clips.find(c => c.id === clip.id)) {
          clips.push(clip);
        }
        break;
      }
      case '[advanced-editor] legacy clip convert success': {
        // Clip already removed optimistically on *started*.
        // Push the new Edit to the front of the list so it appears immediately
        // without needing a full list refetch.
        const { newEdit } = event.payload;
        const advancedEdits = draft.contentHub.recordingInfo.advancedEdits;
        if (!advancedEdits.edits.some(e => e.id === newEdit.id)) {
          advancedEdits.edits.unshift(newEdit);
        }
        break;
      }

      // ── Edit export ────────────────────────────────────────────────────────
      case '[advanced-editor] export edit success': {
        // Optimistically set status to PROCESSING in BOTH the full entity and
        // the list row so both the edit-view header and the edit-list card
        // reflect the frozen state immediately without a refetch.
        // The true terminal status (COMPLETED or FAILED) arrives via Pusher
        // event (TODO) and must also dual-write to both locations.
        const { editId } = event.payload;
        const advancedEdits = draft.contentHub.recordingInfo.advancedEdits;

        // 1. Full entity (drives selectIsEditFrozen + header freeze)
        const entity = advancedEdits.editEntitiesById[editId];
        if (entity) {
          entity.status = 'PROCESSING';
        }

        // 2. List row (drives EditStatusBadge on the edit card)
        const listRow = advancedEdits.edits.find(e => e.id === editId);
        if (listRow) {
          listRow.status = 'PROCESSING';
        }

        break;
      }

      // export edit failed — no state change; current status is preserved.
      // The saga already showed a toast. The header re-enables the button
      // because isFrozen reads the unchanged entity status (DRAFT / FAILED).

      case '[advanced-editor] delete edit success': {
        const { editId } = event.payload;
        const advancedEdits = draft.contentHub.recordingInfo.advancedEdits;

        advancedEdits.edits = advancedEdits.edits.filter(e => e.id !== editId);
        delete advancedEdits.editEntitiesById[editId];
        delete advancedEdits.historyByEditId[editId];
        delete advancedEdits.persistByEditId[editId];
        delete advancedEdits.editVersionByEditId[editId];
        delete advancedEdits.editFetchStatusById[editId];

        if (advancedEdits.activeEditId === editId) {
          advancedEdits.activeEditId = null;
        }
        if (advancedEdits.listTotal > 0) {
          advancedEdits.listTotal -= 1;
        }

        break;
      }

      // ── AI clip suggestions ─────────────────────────────────────────────────

      case '[advanced-editor] trigger ai clip suggestions': {
        draft.contentHub.recordingInfo.aiClipSuggestion.triggerStatus =
          'loading';
        break;
      }

      case '[advanced-editor] trigger ai clip suggestions success': {
        const { suggestion } = event.payload;
        const aiCs = draft.contentHub.recordingInfo.aiClipSuggestion;
        aiCs.triggerStatus = 'idle';
        aiCs.data = suggestion;
        // NOTE: clips are NOT in the POST response — only GET returns them.
        // If state === 'finished', the saga immediately dispatches
        // [advanced-editor] fetch ai clip suggestions which will populate
        // the clips. Do not set fetchStatus = 'success' here.
        break;
      }

      case '[advanced-editor] trigger ai clip suggestions failed': {
        draft.contentHub.recordingInfo.aiClipSuggestion.triggerStatus =
          'failed';
        break;
      }

      case '[advanced-editor] fetch ai clip suggestions': {
        draft.contentHub.recordingInfo.aiClipSuggestion.fetchStatus = 'loading';
        break;
      }

      case '[advanced-editor] fetch ai clip suggestions success': {
        const { suggestion } = event.payload;
        const aiCs = draft.contentHub.recordingInfo.aiClipSuggestion;
        aiCs.fetchStatus = 'success';
        aiCs.data = suggestion;
        break;
      }

      case '[advanced-editor] fetch ai clip suggestions failed': {
        draft.contentHub.recordingInfo.aiClipSuggestion.fetchStatus = 'failed';
        break;
      }

      case '[advanced-editor] ai clip convert started': {
        const { clipIndex } = event.payload;
        draft.contentHub.recordingInfo.aiClipSuggestion.convertingClipIndices[
          clipIndex
        ] = true;
        break;
      }

      case '[advanced-editor] ai clip convert success': {
        const { clipIndex, newEdit } = event.payload;
        const aiCs = draft.contentHub.recordingInfo.aiClipSuggestion;
        delete aiCs.convertingClipIndices[clipIndex];

        // Push the new Edit to the front of the list — same as legacy flow
        // but WITHOUT removing the source AI clip.
        const advancedEdits = draft.contentHub.recordingInfo.advancedEdits;
        if (!advancedEdits.edits.some(e => e.id === newEdit.id)) {
          advancedEdits.edits.unshift(newEdit);
        }

        // Signal editor-v2 to navigate to the new edit.
        aiCs.lastAiConvertedEditId = newEdit.id;
        break;
      }

      case '[advanced-editor] ai clip convert failed': {
        const { clipIndex } = event.payload;
        delete draft.contentHub.recordingInfo.aiClipSuggestion
          .convertingClipIndices[clipIndex];
        break;
      }

      case '[advanced-editor] ai clip convert navigation consumed': {
        // Clear the navigation signal so EditorV2 does not re-trigger the
        // navigate effect when it remounts after the user clicks "back to video".
        draft.contentHub.recordingInfo.aiClipSuggestion.lastAiConvertedEditId =
          null;
        break;
      }
    }
  });
};
